/**
 * 코어 모듈의 한국어 사전. **키의 주인이 이 파일의 주인이다.**
 *
 * ─── 왜 모듈마다 파일이 하나인가 ───
 * 구 stationon-erp 는 로케일이 **6,112줄 단일 파일**이었고 거기에 수직계열 문구가
 * 코어와 뒤섞여 있었다. 그 상태에서는 모듈을 해지해도 문구가 남고, 어떤 줄이 누구 것인지
 * 아무도 모른다. 여기서는 1번 세그먼트가 소유자이므로(`i18n.ts`) 이 파일의 모든 키가
 * `core.` 로 시작해야 하고, `assertMessages` 가 그것을 판정한다 —
 * 남의 접두사를 여기 적으면 게이트가 죽는다.
 *
 * ─── 문장을 쓰는 규칙 ───
 * BI §05: 막다른 화면에 "무엇을 하면 되는지"가 없으면 사용자는 새로고침을 누른다.
 * 그래서 `.next` 키는 **다음 행동**이고, "권한이 없습니다"로 끝내지 않는다 —
 * 누구에게 요청하면 되는지까지 적는다.
 *
 * 한국어 줄바꿈이 `keep-all` 이므로(브랜드 규칙) 긴 문장은 어절이 통째로 넘어간다.
 * 한 문장을 60자 안쪽으로 유지한다 — 그 이상은 좁은 화면에서 어절 하나가 줄을 독차지한다.
 */
import type { MessageDictionary } from '../i18n.js';

export const coreMessagesKo: MessageDictionary = {
  'core.module.label': '코어',

  /*
   * ── 내비 구획 (0057) ───────────────────────────────────────────────
   * 2단 내비의 1단. **모듈 이름이 아니라 하는 일**이라 사용자의 말로 적는다 —
   * '코어'·'전자계약'은 구독 단위의 이름이지 일의 이름이 아니었다.
   *
   * 낱말을 짧게 두는 이유는 폭이다: 넓은 폭 내비가 224px 이고 좌우 여백을 빼면
   * 한 줄에 들어가야 하는 글자가 정해진다. '급여·정산' 처럼 가운뎃점으로 두 낱말을
   * 잇는 것은 그 안에서 두 가지를 말하는 가장 싼 방법이다(`keep-all` 이라 통째로 넘어간다).
   */
  'core.nav.my': '내 것',
  'core.nav.people': '사람',
  'core.nav.attendance': '근태·휴가',
  'core.nav.payroll': '급여·정산',
  'core.nav.settings': '설정',

  // ── 권한 그룹 (역할 편집 화면의 묶음 제목) ──────────────────────────────
  'core.group.rbac': '권한',
  'core.group.module': '모듈',
  'core.group.people': '인사',
  'core.group.org': '조직',
  'core.group.job': '자동 작업',
  'core.group.email': '이메일',
  'core.group.company': '회사',

  // ── permission 라벨. 역할 편집표의 체크박스 이름이다 ────────────────────
  'core.perm.rbac.role.read': '역할 보기',
  'core.perm.rbac.role.manage': '역할 편집',
  'core.perm.module.subscription.manage': '모듈 구독 관리',
  'core.perm.people.employee.read': '직원 보기',
  'core.perm.people.employee.create': '직원 등록',
  'core.perm.people.invite.send': '직원 초대(로그인 허용)',
  'core.perm.people.bank.read': '급여 이체 계좌 보기',
  'core.perm.people.bank.manage': '급여 이체 계좌 관리',
  'core.perm.people.identity.manage': '주민등록번호 관리',
  'core.perm.people.employee.update': '직원 정보 수정',
  'core.perm.org.unit.read': '조직 보기',
  'core.perm.org.unit.manage': '조직 편집',
  'core.perm.org.member.assign': '소속 배정',
  'core.perm.job.run.read': '자동 작업 보기',
  'core.perm.email.template.manage': '이메일 문구 관리',
  'core.perm.email.history.read': '이메일 발송 이력 보기',
  'core.perm.company.settings.manage': '회사 정보 관리',

  // ── 거부 문구. 화면이 막혔을 때 **그 화면 자리에** 뜬다 ─────────────────
  'core.denied.rbac.role.read': '역할과 권한은 권한 관리자만 볼 수 있습니다. 회사 관리자에게 “역할 보기” 권한을 요청하세요.',
  'core.denied.rbac.role.manage': '역할을 바꾸면 다른 사람이 볼 수 있는 것이 함께 바뀝니다. 회사 관리자에게 “역할 편집” 권한을 요청하세요.',
  'core.denied.module.subscription.manage': '모듈 구독은 회사 소유자만 바꿀 수 있습니다. 소유자에게 요청하세요.',
  'core.denied.people.employee.read': '직원 명부는 인사 담당자에게 열려 있습니다. 회사 관리자에게 “직원 보기” 권한을 요청하세요.',
  'core.denied.people.employee.create': '직원 등록은 인사 담당자만 할 수 있습니다. 회사 관리자에게 “직원 등록” 권한을 요청하세요.',
  'core.denied.people.invite.send': '직원 초대는 회사 관리자만 할 수 있습니다. 회사 관리자에게 “직원 초대” 권한을 요청하세요.',
  'core.denied.people.employee.update': '직원 정보 수정은 인사 담당자만 할 수 있습니다. 회사 관리자에게 “직원 정보 수정” 권한을 요청하세요.',
  'core.denied.people.bank.read': '급여 이체 계좌는 인사·급여 담당자에게 열려 있습니다. “급여 이체 계좌 보기” 권한을 요청하세요.',
  'core.denied.people.bank.manage': '급여 이체 계좌를 고치려면 “급여 이체 계좌 관리” 권한이 필요합니다. 회사 관리자에게 요청하세요.',
  'core.denied.people.identity.manage': '주민등록번호를 보거나 고치려면 “주민등록번호 관리” 권한이 필요합니다. 회사 관리자에게 요청하세요.',
  'core.denied.org.unit.read': '조직도는 구성원에게 열려 있습니다. 회사 관리자에게 “조직 보기” 권한을 요청하세요.',
  'core.denied.org.unit.manage': '조직을 바꾸면 그 아래 사람들의 소속이 함께 바뀝니다. 회사 관리자에게 “조직 편집” 권한을 요청하세요.',
  'core.denied.org.member.assign': '사람의 소속은 인사 담당자가 정합니다. 회사 관리자에게 “소속 배정” 권한을 요청하세요.',
  'core.denied.job.run.read': '자동 작업 기록은 회사 관리자에게 열려 있습니다. “자동 작업 보기” 권한을 요청하세요.',
  'core.denied.email.template.manage': '이메일 문구는 회사 관리자에게 열려 있습니다. “이메일 문구 관리” 권한을 요청하세요.',
  'core.denied.email.history.read': '발송 이력은 회사 관리자에게 열려 있습니다. “이메일 발송 이력 보기” 권한을 요청하세요.',
  'core.denied.company.settings.manage': '회사 정보는 회사 관리자만 고칠 수 있습니다. 소유자에게 “회사 정보 관리” 권한을 요청하세요.',

  // ── 역할 템플릿 ────────────────────────────────────────────────────────
  'core.role.owner': '소유자',
  'core.role.owner.desc': '회사의 모든 설정·권한·구독을 관리합니다. 회사를 처음 만들 때 자동으로 부여됩니다.',
  'core.role.member': '구성원',

  // ── 화면 이름. 내비 라벨이자 문서 제목이다 ──────────────────────────────
  // 급여 명세서의 법정공제 항목 이름(`STATUTORY_COMPONENTS`). 코어가 넣는 급여 컴포넌트는 이 다섯뿐이다.
  // 요율은 여기 적지 않는다 — 요율은 정책 데이터이고(`LEGACY_DEDUCTION_POLICY`) 해마다 바뀐다.
  // 문장에 "3.43%" 를 적으면 개정된 뒤에도 화면은 옛 숫자를 말한다.
  'core.payroll.incomeTax': '소득세',
  'core.payroll.localTax': '지방소득세',
  'core.payroll.pension': '국민연금',
  'core.payroll.health': '건강보험',
  'core.payroll.employment': '고용보험',
  /**
   * 시급 근무분. `labelParams.hours` 는 소수 한 자리 문자열이다 — 분을 그대로 보여 주면
   * "450분"이 되고, 사람이 급여명세서에서 읽는 단위는 시간이다.
   */
  'core.payroll.hourlyWork': '시급 근무 ({hours}시간)',
  /**
   * 이관된 지급분. **이름이 내역을 주장하지 않는다** — 구 시스템이 그 내역을 저장하지
   * 않았고(`CORE_EARNING_COMPONENTS` 의 주석), 모르는 것을 아는 척하지 않는 것이 이 줄의 일이다.
   */
  'core.payroll.legacyEarning': '기타 지급분 (구 시스템 이관)',

  'core.screen.people.employees': '직원',
  'core.screen.people.bank': '급여 이체 계좌',
  'core.screen.people.identity': '주민등록번호',
  'core.screen.org.units': '조직',
  'core.screen.rbac.roles': '역할',
  'core.screen.modules': '모듈',
  'core.screen.jobs': '자동 작업',
  'core.screen.email': '이메일',
  'core.screen.notifications': '알림',
  'core.screen.todos': '할일',
  'core.screen.dashboard': '대시보드',
  'core.screen.company': '회사 정보',
  'core.screen.releases': '업데이트 공지',

  /*
   * 대시보드 — **착지 화면.** 카드마다 "없음"과 "0건"이 다른 문장이다:
   * 없는 카드는 아예 안 그려지고(서버가 null 을 준다), 0건은 그 사실을 말한다.
   *
   * ★ 요일 라벨이 다섯 줄 따로 있는 이유는 화면 주석에 있다 — 파생 키로 만들면
   *   i18n 게이트의 소스 스캔이 못 본다. 근태 상태는 `time.screen.attendance.status.*`
   *   를 그대로 쓴다(같은 상태가 두 화면에서 다른 낱말이면 다른 상태로 읽힌다).
   */
  'core.screen.dashboard.loading': '오늘 자리를 불러오는 중입니다',
  'core.screen.dashboard.failed': '대시보드를 불러오지 못했습니다',
  'core.screen.dashboard.today': '오늘 · {date}',
  'core.screen.dashboard.myWeek': '내 이번 주',
  /* 안 읽은 공지. 알림 카드와 **같은 모양이고 다른 사실**이다 — 저건 내 앞으로 온 것이고 이건 제품이 바뀐 것이다. */
  'core.screen.dashboard.releases.some': '새 공지 {n}건',
  // 시작하기(0225). 단계 이름과 "왜 하나" 한 줄.
  'core.screen.dashboard.onboarding': '시작하기',
  'core.screen.dashboard.onboarding.progress': '{done}/{total} 완료',
  'core.screen.dashboard.onboarding.done': '완료',
  'core.screen.dashboard.onboarding.company': '회사 정보 입력',
  'core.screen.dashboard.onboarding.company.why': '상호·사업자등록번호는 급여명세서와 계약서에 찍힙니다.',
  'core.screen.dashboard.onboarding.org': '조직 만들기',
  'core.screen.dashboard.onboarding.org.why': '팀을 먼저 만들어 두면 직원을 등록하면서 바로 배정할 수 있습니다.',
  'core.screen.dashboard.onboarding.people': '직원 등록',
  'core.screen.dashboard.onboarding.people.why': '한 명씩 등록하거나 엑셀로 한 번에 가져올 수 있습니다.',
  'core.screen.dashboard.onboarding.invite': '직원 초대',
  'core.screen.dashboard.onboarding.invite.why': '초대 메일을 받은 직원이 직접 로그인해 출퇴근·휴가를 씁니다.',
  'core.screen.dashboard.onboarding.modules': '쓸 기능 켜기',
  'core.screen.dashboard.onboarding.modules.why': '근태·급여·전자계약 중 필요한 것만 켭니다.',
  'core.screen.dashboard.releases.none': '새 공지가 없습니다',
  'core.screen.dashboard.myBalances': '내 휴가 잔액',
  'core.screen.dashboard.teamVacations': '이번 주 휴가',
  // 기록이 없는 것과 결근은 다른 사실이다. 그래서 "결근"이라고 쓰지 않는다.
  'core.screen.dashboard.noRecord': '기록 없음',
  'core.screen.dashboard.working': '근무 중',
  'core.screen.dashboard.range': '{start} – {end}',
  'core.screen.dashboard.days': '{n}일',
  'core.screen.dashboard.balances.none': '아직 휴가 종류가 없습니다.',
  'core.screen.dashboard.teamVacations.none': '이번 주에 휴가가 없습니다.',

  /*
   * ══ 오늘의 사람 소식 (0054) ═══════════════════════════════════════════════
   *
   * 종류 셋이 **각자 문장**이다. "{name}님의 {kind}" 처럼 조립하면 조사가 깨지고
   * (규칙 30) 파생 키가 생긴다. 그리고 이 카드는 **빈 날에 안 그리므로** "없습니다"
   * 문장이 필요 없다 — 0으로 채운 요약을 안 그리는 것과 같은 판단이다.
   */
  'core.screen.dashboard.people': '오늘의 소식',
  'core.screen.dashboard.people.joined': '입사 첫날',
  'core.screen.dashboard.people.birthday': '생일',
  /* 햇수가 축하의 내용이다. 입사 연차는 사내 공개 정보라 여기 있고, 나이는 없다. */
  'core.screen.dashboard.people.anniversary': '입사 {years}주년',
  'core.screen.dashboard.notices.none': '안 읽은 알림이 없습니다.',
  'core.screen.dashboard.notices.some': '안 읽은 알림 {n}건',
  'core.screen.dashboard.weekday.mon': '월',
  'core.screen.dashboard.weekday.tue': '화',
  'core.screen.dashboard.weekday.wed': '수',
  'core.screen.dashboard.weekday.thu': '목',
  'core.screen.dashboard.weekday.fri': '금',
  'core.screen.my.profile': '내 정보',
  'core.screen.account': '내 계정',

  /*
   * 로그인(0023). **이 문장들만 `core.screen.*` 중에서 부트 전에 읽힌다** —
   * 사전이 부트 응답으로 오는데 그 부트가 401 이라 뜨는 화면이라, 자기 문장을 응답에서
   * 받을 방법이 원리적으로 없다. `PREBOOT_MESSAGES` 가 `core.screen.login.` 접두사를
   * 통째로 골라 간다(locales/preboot.ts) — 그래서 **접두사 밖으로 나가면 조용히 키가 뜬다.**
   */
  /*
   * ══ 개인정보 처리방침 (0055) ══════════════════════════════════════════════
   *
   * 이 접두사도 `PREBOOT_MESSAGES` 가 통째로 가져간다 — 이 화면은 **정의상 부트 전**이다
   * (회사 밖 사람이 읽는 문서라 계정이 없을 수 있다).
   *
   * ★ **본문 문장은 여기 없다.** 방침의 소유자는 `privacy.ts` 의 `PRIVACY_POLICY` 하나이고,
   *   여기 있는 것은 그것을 감싸는 라벨뿐이다. 본문을 사전에 넣으면 법정 고지가 두 곳에
   *   살게 되고, 로케일이 늘 때 한쪽만 번역된 방침이 나간다.
   */
  /*
   * ★ `.title` 인 이유: `PREBOOT_MESSAGES` 는 `core.screen.privacy.` **점까지** 를 접두사로
   *   고르므로 `core.screen.privacy` 자체는 그 그물에 안 걸린다(2026-09-11 시험이 잡았다).
   *   로그인도 같은 모양이다(`core.screen.login.title`).
   */
  'core.screen.privacy.title': '개인정보 처리방침',
  'core.screen.privacy.effective': '시행일',
  'core.screen.privacy.provider': '서비스 제공자',
  'core.screen.privacy.provider.name': '상호',
  'core.screen.privacy.provider.representative': '대표자',
  'core.screen.privacy.provider.businessNumber': '사업자등록번호',
  'core.screen.privacy.provider.address': '주소',
  'core.screen.privacy.items': '처리하는 개인정보 항목',
  'core.screen.privacy.purpose': '목적',
  'core.screen.privacy.retention': '보유기간',
  'core.screen.privacy.processors': '처리 위탁',
  'core.screen.privacy.controller': '개인정보 보호책임자',
  'core.screen.privacy.controller.name': '성명',
  'core.screen.privacy.controller.role': '직책',
  'core.screen.privacy.controller.contact': '연락처',
  'core.screen.privacy.back': '돌아가기',
  /* 개발에서만 보인다 — 프로덕션은 이 상태로 기동하지 않는다. */
  'core.screen.privacy.gaps': '아직 채워지지 않은 자리가 있습니다',
  'core.screen.privacy.link': '개인정보 처리방침',

  'core.screen.login.title': '로그인',
  'core.screen.login.email': '이메일',
  'core.screen.login.password': '비밀번호',
  'core.screen.login.submit': '로그인',
  'core.screen.login.submitting': '확인하는 중…',
  /*
   * 거부. **어느 쪽이 틀렸는지 말하지 않는다** — 구분해 주면 이 화면이 계정 열거 도구가 된다.
   * 서버도 같은 이유로 구분하지 않고(`auth/service.ts`), 두 경우에 같은 시간을 쓴다.
   */
  'core.screen.login.rejected': '이메일 또는 비밀번호가 올바르지 않습니다.',
  'core.screen.login.rateLimited': '로그인 시도가 너무 많습니다. 잠시 뒤에 다시 시도하세요.',
  'core.screen.login.rateLimited.after': '로그인 시도가 너무 많습니다. 약 {minutes}분 뒤에 다시 시도하세요.',
  /*
   * ★ 2026-09-08(0036)에 이 문장이 바뀌었다. 그전에는 "회사 관리자에게 요청하세요"였고
   * 그 이유가 *"메일 발송 경로가 0줄이라, 누르면 아무 일도 안 하는 링크는 그 기능이
   * 있다고 말하는 UI"* 였다. 이제 그 경로가 있으므로 링크를 그린다 —
   * **없어진 제약의 문장을 남겨 두면 그것이 다음 거짓말이다.**
   */
  /*
   * 회사 선택 (0056). 접두사가 `core.screen.login.` 이라 `PREBOOT_MESSAGES` 가 자동으로
   * 골라 간다 — **이 화면도 정의상 부트 전이다.** 접두사 밖으로 나가면 화면에 키 문자열이
   * 그대로 뜨고, 그것을 잡는 게이트가 하나도 없다.
   */
  'core.screen.login.noAccount': '아직 계정이 없나요?',
  'core.screen.login.signup': '회원가입',

  /*
   * ══ 셀프 가입 · 소셜 · 초대 (0225) ═════════════════════════════════════════
   *
   * 세 접두사(`signup.` · `invite.` · `social.`) 모두 `PREBOOT_MESSAGES` 가 통째로 가져간다 —
   * 이 화면들은 정의상 부트 전이다. **접두사 밖으로 나가면 화면에 키가 그대로 뜬다.**
   * 자리표시 뒤에 조사를 붙이지 않는다(규칙 30).
   */
  'core.screen.signup.title': '회원가입',
  'core.screen.signup.intro': '회사 계정을 만들고 바로 시작하세요. 직원 초대·근태·급여까지 한 곳에서 관리합니다.',
  'core.screen.signup.email': '업무용 이메일',
  'core.screen.signup.submit': '이메일로 가입하기',
  'core.screen.signup.submitting': '보내는 중…',
  'core.screen.signup.consentNote': '가입하면 서비스 이용과 개인정보 처리에 동의하게 됩니다.',
  'core.screen.signup.toLogin': '로그인 화면으로',
  'core.screen.signup.sent.title': '메일함을 확인하세요',
  'core.screen.signup.sent': '가입을 마저 진행할 링크를 보냈습니다: {email}',
  'core.screen.signup.sent.next': '메일이 안 보이면 스팸함을 확인하세요. 링크는 24시간 동안 쓸 수 있습니다.',
  'core.screen.signup.sent.retry': '다른 이메일로 다시 보내기',
  'core.screen.signup.rateLimited': '요청이 너무 많습니다. 잠시 뒤에 다시 시도하세요.',
  'core.screen.signup.rateLimited.after': '요청이 너무 많습니다. 약 {minutes}분 뒤에 다시 시도하세요.',
  'core.screen.signup.restart': '회원가입 처음부터',
  'core.screen.signup.complete.title': '회사 만들기',
  'core.screen.signup.complete.loading': '가입 정보를 확인하는 중…',
  'core.screen.signup.complete.introEmail': '로그인 이메일: {email}',
  'core.screen.signup.complete.introSocial': '{provider} 계정으로 가입합니다 · {email}',
  'core.screen.signup.complete.name': '내 이름',
  'core.screen.signup.complete.company': '회사 이름',
  'core.screen.signup.complete.company.hint': '나중에 바꿀 수 있음',
  'core.screen.signup.complete.phone': '휴대폰 번호',
  'core.screen.signup.complete.phone.hint': '내 직원 정보의 연락처로 저장',
  'core.screen.signup.complete.phoneOptional': '휴대폰 번호 (선택)',
  'core.screen.signup.complete.password': '비밀번호',
  'core.screen.signup.complete.password.hint': '{min}자 이상',
  'core.screen.signup.complete.passwordOptional': '비밀번호 (선택)',
  'core.screen.signup.complete.passwordOptional.hint': '비우면 소셜로만 로그인 · {min}자 이상',
  'core.screen.signup.complete.agree': '서비스 이용과 개인정보 처리에 동의합니다.',
  'core.screen.signup.complete.submit': '회사 만들고 시작하기',
  'core.screen.signup.complete.submitting': '회사를 만드는 중…',

  'core.screen.invite.title': '초대 수락',
  'core.screen.invite.loading': '초대를 확인하는 중…',
  'core.screen.invite.heading': '초대받은 회사: {company}',
  'core.screen.invite.intro': '{name} 님 · 로그인 이메일 {email}. 로그인 방법을 정하면 바로 시작합니다.',
  'core.screen.invite.password': '사용할 비밀번호',
  'core.screen.invite.submit': '비밀번호 정하고 시작하기',
  'core.screen.invite.submitting': '시작하는 중…',

  /* 버튼 문구는 각 사 가이드의 허용 문구다 — 카카오는 "카카오 로그인" 외에 허용되지 않는다. */
  'core.screen.social.kakao': '카카오 로그인',
  'core.screen.social.naver': '네이버로 시작하기',
  'core.screen.social.google': 'Google로 계속하기',
  // Apple 의 허용 문구(Apple로 로그인 · Apple로 가입 · Apple로 계속하기) — iOS 앱만 그린다(0227).
  'core.screen.social.apple': 'Apple로 로그인',
  'core.screen.social.fake': '개발용 소셜 로그인',
  /* 계정 삭제(0229). 웹 「내 계정」과 앱 계정 화면이 같은 문장을 쓴다. */
  'core.screen.account.delete': '계정 삭제',
  'core.screen.account.delete.why':
    '삭제를 요청하면 모든 기기에서 로그아웃되고, 30일 뒤 로그인 방법(비밀번호·소셜 연결)이 지워집니다. '
    + '그 안에 다시 로그인하면 삭제가 취소됩니다. 회사가 보관하는 근태·급여 기록은 회사의 보존 의무에 따라 남습니다.',
  'core.screen.account.delete.open': '계정 삭제하기',
  'core.screen.account.delete.confirm.title': '계정을 삭제할까요?',
  'core.screen.account.delete.confirm.body':
    '지금 모든 기기에서 로그아웃됩니다. 30일 안에 다시 로그인하지 않으면 계정이 지워집니다. '
    + '이 회사에 로그인할 수 있는 사람이 나뿐이면 회사도 함께 닫힙니다.',
  'core.screen.account.delete.cancel': '취소',
  'core.screen.account.delete.submit': '삭제 요청',
  'core.screen.account.delete.submitting': '요청하는 중…',
  'core.screen.account.delete.done.title': '삭제를 요청했습니다',
  'core.screen.account.delete.done': '{date} 이후 계정이 지워집니다. 그 전에 다시 로그인하면 취소됩니다.',
  'core.screen.account.delete.done.company': '이 회사에 로그인할 사람이 남지 않아 회사도 함께 닫힙니다.',
  'core.screen.account.delete.done.ok': '확인',
  'core.screen.social.name.kakao': '카카오',
  'core.screen.social.name.naver': '네이버',
  'core.screen.social.name.google': 'Google',
  'core.screen.social.name.apple': 'Apple',
  'core.screen.social.name.fake': '개발용',
  'core.screen.social.or': '또는',
  'core.screen.social.app.title': '앱으로 돌아갑니다',
  'core.screen.social.app.intro': '로그인을 마쳤습니다. 앱이 자동으로 열리지 않으면 아래 버튼을 누르세요.',
  'core.screen.social.app.open': 'Worhythm 앱 열기',
  'core.screen.social.app.signupNote': '처음이면 소셜 계정으로 바로 회사를 만들 수 있습니다.',
  'core.screen.social.callback.title': '소셜 계정 확인',
  'core.screen.social.callback.working': '계정을 확인하는 중…',
  'core.screen.social.choose.hint': '이 소셜 계정이 여러 회사에 연결되어 있습니다.',
  'core.screen.login.choose.title': '어느 회사로 로그인할까요?',
  'core.screen.login.choose.hint': '이 이메일로 여러 회사에 등록되어 있습니다.',
  'core.screen.login.choose.back': '다른 계정으로 로그인',
  'core.screen.login.forgot': '비밀번호를 잊으셨나요?',
  // 앱(0218): 로그인은 됐는데 세션 쿠키가 저장되지 않았다. 사용자가 할 수 있는 것은 다시 시도와 문의뿐이다.
  'core.screen.login.sessionNotKept': '로그인은 됐지만 이 기기에 로그인 상태가 저장되지 않았습니다.',
  'core.screen.login.sessionNotKept.next': '다시 시도해도 같으면 관리자에게 알려 주세요.',
  'core.screen.logout': '로그아웃',

  /*
   * 비밀번호 재설정(0036). **로그인 화면과 같은 이유로 부트 전에 읽힌다** —
   * 이 화면들은 정의상 로그인하지 못한 사람이 보는 자리라, 사전을 부트 응답에서 받을
   * 방법이 원리적으로 없다. `PREBOOT_MESSAGES` 가 `core.screen.password.` 접두사를
   * 통째로 골라 간다 — **접두사 밖으로 나가면 조용히 키가 뜬다.**
   */
  'core.screen.password.forgot.title': '비밀번호 재설정',
  'core.screen.password.forgot.intro': '가입한 이메일 주소를 넣으면 재설정 링크를 보내 드립니다.',
  'core.screen.password.forgot.email': '이메일',
  'core.screen.password.forgot.submit': '재설정 링크 보내기',
  'core.screen.password.forgot.submitting': '보내는 중…',
  /*
   * ★ 성공 문장이 "보냈습니다"가 아니라 **"가입돼 있다면 보냈습니다"** 다.
   * 서버가 계정의 존재를 안 알려 주는데(계정 열거) 화면이 "보냈습니다"라고 하면
   * 그 화면이 서버가 숨긴 것을 대신 말해 버린다 — 그리고 없는 계정에는 거짓말이다.
   */
  'core.screen.password.forgot.done': '그 주소로 가입돼 있다면 재설정 링크를 보냈습니다. 메일함을 확인하세요.',
  'core.screen.password.forgot.done.next': '메일이 안 보이면 스팸함도 확인하고, 몇 분 뒤에 다시 시도하세요.',
  'core.screen.password.forgot.rateLimited': '요청이 너무 많습니다. 잠시 뒤에 다시 시도하세요.',
  'core.screen.password.forgot.rateLimited.after': '요청이 너무 많습니다. 약 {minutes}분 뒤에 다시 시도하세요.',
  'core.screen.password.back': '로그인으로 돌아가기',

  'core.screen.password.reset.title': '새 비밀번호 정하기',
  'core.screen.password.reset.intro': '새 비밀번호를 정하면 지금 로그인돼 있는 모든 기기가 로그아웃됩니다.',
  'core.screen.password.reset.new': '새 비밀번호',
  'core.screen.password.reset.confirm': '새 비밀번호 확인',
  /* `{min}` 의 원본은 `MIN_PASSWORD_LENGTH` 다. 화면이 그 값을 넘긴다 — 여기 숫자를 안 적는다. */
  'core.screen.password.reset.rule': '({min}자 이상)',
  'core.screen.password.reset.mismatch': '위에 입력한 새 비밀번호와 다릅니다.',
  'core.screen.password.reset.tooShort': '{min}자 이상으로 입력해 주세요.',
  'core.screen.password.reset.submit': '비밀번호 정하기',
  'core.screen.password.reset.submitting': '정하는 중…',
  'core.screen.password.reset.done': '비밀번호를 정했습니다. 새 비밀번호로 로그인하세요.',
  /* 링크 없이 이 경로를 연 경우. 만료된 링크와 **다른 문장**이다 — 할 일이 다르다. */
  'core.screen.password.reset.noToken': '재설정 링크가 없습니다. 메일에 있는 링크로 다시 열어 주세요.',

  /*
   * 역할 화면. 편집은 2026-09-03 에 열렸다 — `readOnly`("편집은 곧 열립니다")는 그날
   * 거짓이 되어 지웠다. **없어진 기능의 문장은 남겨 두면 화면 어디에도 안 뜨는 거짓말이다.**
   */
  'core.screen.rbac.roles.empty': '아직 역할이 없습니다',
  'core.screen.rbac.roles.empty.next': '회사를 만들 때 기본 역할이 생깁니다. 관리자에게 문의하세요.',
  'core.screen.rbac.roles.select': '왼쪽에서 역할을 고르면 그 역할이 가진 권한이 보입니다',
  'core.screen.rbac.roles.managed': '제품 관리',
  'core.screen.rbac.roles.managed.hint': '제품이 관리하는 역할입니다. 릴리스마다 권한이 갱신됩니다.',
  'core.screen.rbac.roles.custom': '회사 지정',
  'core.screen.rbac.roles.granted': '부여됨',
  'core.screen.rbac.roles.grantedCount': '권한 {count}개',
  'core.screen.rbac.roles.destructive': '되돌릴 수 없음',
  'core.screen.rbac.roles.opens': '열리는 화면',
  'core.screen.rbac.roles.opensNone': '화면 없이 동작만 여는 권한입니다',
  'core.screen.rbac.roles.catalogEmpty': '이 회사가 줄 수 있는 권한이 없습니다',
  'core.screen.rbac.roles.catalogEmpty.next': '모듈 구독이 꺼져 있으면 그 모듈의 권한은 줄 수 없습니다.',

  /*
   * 편집. 제품 관리 역할은 **직접 못 고친다** — DB 제약이 그렇게 되어 있다
   * (`rp_managed_is_system_ck`: 관리형 역할에는 테넌트 권한이 존재할 수 없다).
   * 그래서 "고칠 수 없습니다"로 끝내지 않고 **할 수 있는 일**을 제안한다: 복제해서 고친다.
   */
  'core.screen.rbac.roles.fork': '복제해서 편집',
  'core.screen.rbac.roles.fork.description': '이 역할의 권한을 그대로 복제해 회사 전용 역할을 만듭니다. 원본은 그대로 두고 복제본만 고칩니다.',
  'core.screen.rbac.roles.fork.name': '새 역할 이름',
  'core.screen.rbac.roles.fork.submit': '복제',
  'core.screen.rbac.roles.fork.submitting': '복제 중…',
  'core.screen.rbac.roles.fork.cancel': '취소',
  'core.screen.rbac.roles.fork.hint': '제품이 관리하는 역할이라 직접 고칠 수 없습니다. 복제하면 이 회사만의 역할이 되고, 릴리스에 덮이지 않습니다.',
  'core.screen.rbac.roles.grant': '권한 부여',
  'core.screen.rbac.roles.saving': '저장 중…',

  /*
   * ── 구성원 · 이름 · 보관 (0050) ──────────────────────────────────────
   * 표(`core.role_assignments`)와 DB 불변식 셋은 0005 가 만들어 뒀는데 **문도 화면도
   * 없었다** — 회사가 새 직원에게 역할을 줄 방법이 제품 안에 0개였다(0049 §4).
   *
   * 빈 목록 문장이 이 묶음에서 가장 중요하다: 권한을 켜 놓고 아무에게도 안 준 역할은
   * 아무 일도 하지 않는다. 그 사실을 안 적으면 "권한을 줬는데 안 되는데요"가 된다.
   */
  'core.screen.rbac.roles.members': '구성원',
  'core.screen.rbac.roles.members.count': '{count}명',
  'core.screen.rbac.roles.members.empty': '이 역할을 가진 사람이 없습니다. 권한을 켜 두어도 배정하기 전까지는 아무것도 열리지 않습니다.',
  'core.screen.rbac.roles.members.since': '{at} 배정',
  'core.screen.rbac.roles.members.remove': '빼기',
  'core.screen.rbac.roles.members.add': '구성원 추가',
  /*
   * ★ 브라우저에서 `우리 회사 소유자 에 배정` 으로 떴다(2026-09-11). 규칙 30 이
   * "`{...}` 뒤에 조사를 붙이지 않는다"라고 적어 둔 자리이고, 붙이면 띄어쓰기가 어색해지고
   * 안 붙이면 받침에 따라 틀린다. 그래서 **이름을 문장 끝으로 보낸다.**
   */
  'core.screen.rbac.roles.members.add.title': '구성원 추가 · {name}',
  'core.screen.rbac.roles.members.add.description': '사번이나 이름으로 찾아 배정합니다. 이미 이 역할인 사람은 목록에 없습니다.',
  'core.screen.rbac.roles.members.add.search': '사번 · 이름',
  'core.screen.rbac.roles.members.add.searching': '찾는 중…',
  'core.screen.rbac.roles.members.add.none': '조건에 맞는 직원이 없습니다.',
  'core.screen.rbac.roles.members.add.confirm': '배정',

  'core.screen.rbac.roles.custom.hint': '이 회사가 만든 역할입니다. 권한·이름을 고칠 수 있고 릴리스에 덮이지 않습니다.',
  'core.screen.rbac.roles.archived.hint': '보관된 역할입니다. 구성원은 남아 있지만 이 역할로는 아무것도 열리지 않고, 새로 배정할 수도 없습니다.',
  'core.screen.rbac.roles.rename': '이름 바꾸기',
  'core.screen.rbac.roles.rename.title': '역할 이름 바꾸기',
  'core.screen.rbac.roles.rename.description': '사람이 읽는 이름만 바뀝니다. 권한과 구성원은 그대로입니다.',
  'core.screen.rbac.roles.rename.submit': '저장',
  'core.screen.rbac.roles.rename.error.name': '같은 이름의 역할이 이미 있습니다.',
  'core.screen.rbac.roles.archive': '보관',
  'core.screen.rbac.roles.archive.title': '{name} 보관',
  'core.screen.rbac.roles.archive.description': '삭제가 아닙니다. 언제든 되돌릴 수 있고 배정도 남습니다. 다만 보관하는 동안 이 역할로는 아무 권한도 열리지 않습니다.',
  'core.screen.rbac.roles.archive.members': '지금 이 역할인 {count}명이 이 역할이 주던 권한을 잃습니다.',
  'core.screen.rbac.roles.archive.membersUnknown': '구성원 목록을 아직 못 읽었습니다. 몇 명이 권한을 잃는지 확인한 뒤 진행하세요.',
  'core.screen.rbac.roles.unarchive': '보관 해제',

  /*
   * ── 모듈 구독 화면 ──────────────────────────────────────────────────
   * 이 화면의 존재 이유는 역할 편집표와 **같다**: "이걸 켜면/주면 무엇이 열리는가".
   * 모듈 이름만 나열하면 관리자는 `pay` 를 끄면 무엇이 사라지는지 모르고, 모르면
   * 끄지도 켜지도 못한다. 그래서 줄마다 열리는 화면을 함께 적는다.
   *
   * 해지 문구가 "정말 삭제하시겠습니까?"가 아닌 이유: 해지는 삭제가 아니다.
   * 설정(`tenant_modules.config`)은 남고 다시 켜면 돌아온다 — 그 사실을 말해야
   * 사용자가 겁먹지 않고 결정할 수 있다(BI §05: 다음 행동을 준다).
   */
  'core.screen.modules.intro': '켜 둔 모듈의 메뉴와 권한만 이 회사에서 열립니다.',
  'core.screen.modules.empty': '표시할 모듈이 없습니다',
  'core.screen.modules.empty.next': '카탈로그가 아직 동기화되지 않았습니다. 관리자에게 문의하세요.',
  'core.screen.modules.enabled': '사용 중',
  'core.screen.modules.disabled': '꺼짐',
  /* 플랫폼 모듈. **비활성 토글을 그리지 않는다** — 그러면 권한이 없어서 못 끄는 줄 안다. */
  'core.screen.modules.always': '항상 켜짐',
  'core.screen.modules.always.hint': '이 모듈을 끄면 구독을 되살릴 화면까지 닫히기 때문에 끌 수 없습니다.',
  'core.screen.modules.private': '이 회사 전용',
  // 구독 한 줄(0219). 웹은 팔지 않는다 — 앱에서 산다.
  'core.screen.modules.billing.free': '재직 {count}명 · {free}명까지는 무료라 지금은 낼 것이 없습니다.',
  'core.screen.modules.billing.tier': '재직 {count}명 · 최대 {tier}명 구간이 맞습니다.',
  'core.screen.modules.billing.overLargest': '재직 {count}명 · {largest}명을 넘는 회사는 상담으로 정합니다.',
  'core.screen.modules.billing.uncovered': '지금 구독이 켜 둔 모듈과 인원을 다 덮지 못합니다. 사용은 막히지 않습니다.',
  'core.screen.modules.billing.inApp': '구독은 모바일 앱의 내 계정 › 구독에서 합니다.',
  'core.screen.modules.billing.failed': '구독 상태를 불러오지 못했습니다. 모듈은 그대로 쓸 수 있습니다.',
  'core.screen.modules.private.hint': '이 회사만을 위해 만든 모듈이라 다른 회사에는 보이지 않습니다. 켜고 끄려면 서비스 운영자에게 요청하세요.',
  /* 자격증명 폼(0060). **모듈 이름이 여기 없다** — 폼은 선언에서 생기고 코어는 vertical 을 모른다. */
  'core.screen.modules.credentials': '연동에 필요한 값',
  'core.screen.modules.cred.configured': '설정됨 (끝 {hint})',
  'core.screen.modules.cred.empty': '아직 넣지 않았습니다',
  'core.screen.modules.cred.optional': '선택',
  'core.screen.modules.cred.save': '저장',
  'core.screen.modules.cred.replace': '교체',
  'core.screen.modules.cred.remove': '지우기',
  'core.screen.modules.cred.test': '연결 테스트',
  'core.screen.modules.cred.testBlocked': '필수 값을 모두 넣어야 테스트할 수 있습니다.',
  'core.screen.modules.cred.testOk': '연결됨 ({adapter})',
  'core.screen.modules.cred.testFail': '연결되지 않았습니다 ({adapter})',
  'core.screen.modules.opens': '열리는 화면',
  'core.screen.modules.opensNone': '화면 없이 동작만 여는 모듈입니다',
  'core.screen.modules.enable': '사용 시작',
  'core.screen.modules.enabling': '켜는 중…',
  'core.screen.modules.disable': '해지',
  'core.screen.modules.disabling': '해지 중…',
  'core.screen.modules.disable.title': '{name} 해지',
  'core.screen.modules.disable.description': '이 회사의 모든 사용자에게서 아래 화면과 권한이 사라집니다. 설정은 지워지지 않아서, 다시 켜면 그대로 돌아옵니다.',
  'core.screen.modules.disable.none': '이 모듈에는 화면이 없습니다. 사라지는 것은 권한뿐입니다.',
  'core.screen.modules.disable.confirm': '해지',
  'core.screen.modules.disable.cancel': '취소',
  /*
   * 구독은 바뀌었는데 **메뉴를 새로 못 받은** 상태. 저장 실패가 아니므로 그렇게 말하지
   * 않는다 — 여기서 "저장에 실패했습니다"를 띄우면 사용자가 같은 토글을 다시 누른다.
   */
  'core.screen.modules.navStale': '구독은 바뀌었지만 메뉴를 새로 받지 못했습니다.',
  'core.screen.modules.navStale.next': '새로고침',

  /*
   * 직원 목록의 문구. 화면 하나가 이만큼의 키를 갖는 것이 정상이다 —
   * 빈 상태가 둘(등록 전 / 검색 결과 없음)이고 각각 다음 행동이 다르기 때문이다.
   */
  'core.screen.people.employees.col.no': '사번',
  'core.screen.people.employees.col.name': '이름',
  'core.screen.people.employees.col.email': '이메일',
  'core.screen.people.employees.col.org': '소속',
  // 로그인 상태와 초대(0225).
  'core.screen.people.employees.col.access': '로그인',
  'core.screen.people.access.active': '사용 중',
  'core.screen.people.access.invited': '초대됨',
  'core.screen.people.access.none': '없음',
  'core.screen.people.invite': '초대',
  'core.screen.people.invite.manage': '초대 관리',
  'core.screen.people.invite.title': '{name} 님 초대',
  'core.screen.people.invite.confirm': '{email} 주소로 초대 메일을 보냅니다. 받은 사람이 링크에서 비밀번호를 정하거나 소셜 계정으로 시작합니다.',
  'core.screen.people.invite.pending': '{email} 주소로 보낸 초대가 아직 수락되지 않았습니다. 다시 보내면 이전 링크는 쓸 수 없게 됩니다.',
  'core.screen.people.invite.noEmail': '이메일이 없어 초대할 수 없습니다. 직원 정보에 이메일을 먼저 넣으세요.',
  'core.screen.people.invite.send': '초대 메일 보내기',
  'core.screen.people.invite.resend': '다시 보내기',
  'core.screen.people.invite.sending': '보내는 중…',
  'core.screen.people.invite.revoke': '초대 취소',
  'core.screen.people.invite.revoking': '취소하는 중…',
  'core.screen.people.invite.close': '닫기',
  'core.screen.people.invite.sent': '{name} 님에게 초대 메일을 보냈습니다.',
  'core.screen.people.invite.revoked': '{name} 님의 초대를 취소했습니다.',
  'core.screen.people.invite.failedAfterCreate': '직원은 등록했지만 초대 메일을 보내지 못했습니다 — {reason}. 목록의 “초대”로 다시 보낼 수 있습니다.',
  'core.screen.people.employees.form.invite': '초대 메일 보내기',
  'core.screen.people.employees.form.invite.hint': '이 이메일로 로그인 링크가 갑니다. 끄면 명부에만 올라가고 나중에 초대할 수 있습니다.',
  'core.screen.people.employees.col.joinedOn': '입사일',
  /*
   * 입사일 없음. 이관된 행에만 있을 수 있는 상태이고(새 등록은 필수다), **비워 두지
   * 않는다** — 이 값이 없으면 자동 부여가 그 사람을 통째로 건너뛴다.
   * 구 프로덕션에 4명 있었고 구 시스템은 그 8건을 그냥 부여했다.
   */
  'core.screen.people.employees.noJoinedOn': '모름',
  /* `Field.hint` 는 라벨 **옆**에 붙는다 — 긴 문장을 넣으면 라벨이 문단이 된다
   * (근태 다이얼로그의 `hourlyMinutes.hint` 와 같은 자리). 짧게 둔다. */
  /* 힌트는 라벨 **옆**에 붙는다 — 괄호가 없으면 "입사일 자동 부여 기준" 한 덩어리로
   * 읽힌다(브라우저에서 봤다). 같은 폼의 `(선택)` 과 같은 모양으로 맞춘다. */
  'core.screen.people.employees.form.joinedOn.hint': '(자동 부여 기준)',
  /*
   * 소속 없음. **빈 칸으로 두지 않는다** — 빈 칸은 "아직 안 불러왔다"로 읽히고,
   * 구 시스템의 조직도는 소속 없는 사람을 아예 안 그려서 7명이 화면에서 사라졌다.
   * 없는 것은 없다고 적는다(이메일의 `noEmail` 과 같은 판단).
   */
  'core.screen.people.employees.noOrg': '미배정',
  // 사진(0030). ★ `{name}` 뒤에 조사를 붙이지 않는다 — 받침이 사용자 데이터에 딸려 있다.
  'core.screen.people.employees.photo.change': '{name} 프로필 사진 바꾸기',
  'core.screen.people.employees.photo.uploading': '사진을 올리는 중입니다',
  // 상태 코드마다 사람이 할 일이 다르므로 문장도 다르다. "실패했습니다" 하나로 접지 않는다.
  'core.screen.people.employees.photo.tooLarge': '2MB 이하 사진만 올릴 수 있습니다.',
  'core.screen.people.employees.photo.badType': 'JPG · PNG · WebP 만 올릴 수 있습니다.',
  'core.screen.people.employees.photo.remove': '사진 지우기',
  'core.screen.people.employees.photo.remove.title': '{name} 님의 사진을 지웁니다',
  'core.screen.people.employees.photo.remove.confirm': '되돌릴 수 없습니다. 필요하면 다시 올려야 합니다.',
  'core.screen.people.employees.photo.remove.cancel': '닫기',
  'core.screen.people.employees.photo.removing': '지우는 중…',
  'core.screen.people.employees.photo.failed': '사진을 올리지 못했습니다. 잠시 후 다시 시도해 주세요.',
  'core.screen.people.employees.filterOrg': '소속',
  'core.screen.people.employees.filterOrg.all': '전체',
  'core.screen.people.employees.filterOrg.none': '미배정',
  'core.screen.people.employees.actions': '작업',
  'core.screen.people.employees.assign': '소속 변경',
  'core.screen.people.employees.assign.title': '{name} 소속',
  'core.screen.people.employees.assign.description': '조직을 고르면 그 단위의 인원에 포함됩니다. 비우면 미배정이 됩니다.',
  'core.screen.people.employees.assign.field': '소속',
  'core.screen.people.employees.assign.none': '미배정',
  'core.screen.people.employees.assign.empty': '아직 조직이 없습니다. 조직 화면에서 먼저 만드세요.',
  'core.screen.people.employees.assign.submit': '저장',
  'core.screen.people.employees.assign.submitting': '저장 중…',
  'core.screen.people.employees.assign.cancel': '취소',
  'core.screen.people.employees.noEmail': '없음',
  'core.screen.people.employees.search': '검색',
  'core.screen.people.employees.search.placeholder': '사번 또는 이름',
  'core.screen.people.employees.empty': '아직 등록된 직원이 없습니다',
  'core.screen.people.employees.empty.next': '첫 직원을 등록하면 근태와 급여를 여기서부터 연결할 수 있습니다.',
  'core.screen.people.employees.noMatch': '검색과 맞는 직원이 없습니다',
  'core.screen.people.employees.noMatch.next': '사번 일부나 이름의 앞 글자로 다시 찾아보세요.',
  /*
   * **범위 밖 페이지.** 세 번째 빈 상태다 — 실측(2026-09-02)에서 `?offset=1000` 이
   * "아직 등록된 직원이 없습니다"를 띄웠는데 실제로는 63명이 있었다. 그건 거짓말이고,
   * 그 화면에서 사용자가 할 일(첫 페이지로)이 등록과 완전히 다르다.
   * 상한(`maxOffset`) 초과로 422 가 온 경우도 여기로 모은다 — 사용자가 할 수 있는 일이
   * 같기 때문이다. 레지스트리의 폼 문구("표시된 항목을 고치면 저장됩니다")는
   * 폼이 없는 이 화면에서 아무것도 가리키지 않는다.
   */
  'core.screen.people.employees.outOfRange': '이 페이지에는 직원이 없습니다',
  'core.screen.people.employees.outOfRange.next': '목록이 짧아졌거나 주소의 페이지 번호가 범위를 벗어났습니다.',
  'core.screen.people.employees.firstPage': '첫 페이지로',
  'core.screen.people.employees.pager': '페이지 이동',
  'core.screen.people.employees.prev': '이전',
  'core.screen.people.employees.next': '다음',
  // 총 개수를 세지 않으므로(count(*) 는 큰 테이블에서 비싸다) "몇 개 중"이 없다.
  'core.screen.people.employees.range': '{from}–{to}번째',

  /*
   * 등록 폼. 사번·이름만 필수다 — 서버 zod 와 같다(people.ts).
   * `.duplicate` 는 **칸에 붙는** 문장이라 '{field}' 를 받는다. 레지스트리의
   * `core.error.resource.conflict.next` 를 그대로 쓰지 않는 이유가 이것이다:
   * 그건 서버가 보낸 raw 필드명('employeeNo')을 끼워 넣어서 화면에 없는 단어가 뜬다.
   */
  'core.screen.people.employees.create': '직원 등록',
  /* ★ 이 문장은 **한 번 거짓이 됐다**(2026-09-04, 브라우저에서 봤다): 입사일이 필수 칸이
   * 됐는데 여기는 "사번과 이름만 있으면" 그대로였다. 필수 칸이 늘면 이 줄도 함께 는다 —
   * 폼이 거절하는 이유를 설명문이 부정하고 있으면 사용자는 버튼이 고장 났다고 읽는다. */
  'core.screen.people.employees.create.description': '사번·이름·입사일이 필요합니다. 나머지는 나중에 채울 수 있습니다.',
  'core.screen.people.employees.form.phone': '연락처',
  'core.screen.people.employees.form.optional': '(선택)',
  'core.screen.people.employees.form.duplicate': '이미 등록된 {field}입니다',
  'core.screen.people.employees.form.cancel': '취소',
  'core.screen.people.employees.form.submit': '등록',
  // 보내는 중임을 버튼 자신이 말한다. 별도 스피너를 띄우면 버튼과 스피너 둘을 봐야 한다.
  'core.screen.people.employees.form.submitting': '등록 중…',

  /*
   * ── 셸의 문구 ───────────────────────────────────────────────────────
   * 선언(매니페스트)에서 오지 않고 코어 화면 코드가 직접 부르는 키들이다.
   * `i18n:check` 의 사용처 스캔이 이 줄들을 대상으로 삼는다 — 오타가 나면
   * 게이트가 잡고, 못 잡으면 화면에 키가 그대로 뜬다(폴백을 만들지 않는다).
   */
  'core.screen.nav': '주 메뉴',
  /*
   * 좁은 폭에서 내비가 서랍이 된다(shell.tsx, 768px). 그 여닫이 버튼의 **접근 가능한 이름**이라
   * 눈에 보이는 라벨이 아니다 — 버튼 안에는 그린 아이콘만 있다. 그래서 '메뉴'가 아니라
   * 하는 일을 적는다: 스크린리더가 읽는 것이 곧 그 버튼을 눌렀을 때 일어나는 일이어야 한다.
   */
  'core.screen.nav.open': '메뉴 열기',
  'core.screen.nav.close': '메뉴 닫기',
  'core.screen.breadcrumbs': '현재 위치',
  /*
   * 구획 접기 버튼의 접근 가능한 이름 — `aria-expanded` 가 열림/닫힘을 이미 말하므로
   * 문구는 **무엇의 구획인가**만 말한다. "펼치기/접기"를 문구에 넣으면 스크린리더가
   * 상태를 두 번 읽고(속성 + 문구), 그 둘이 어긋나는 순간이 반드시 온다.
   */
  'core.screen.nav.section': '{name} 메뉴',
  'core.screen.loading': '불러오는 중',
  'core.screen.unknown': '알 수 없는 화면',
  'core.screen.noScreens': '아직 열 수 있는 화면이 없습니다',
  'core.screen.noScreens.description': '계정은 만들어졌지만 역할이 아직 부여되지 않았습니다. 역할을 받으면 이 자리에 메뉴가 나타납니다.',
  'core.screen.noScreens.next': '회사 관리자에게 역할 배정을 요청하세요.',

  /*
   * 막힌 경로의 세 갈래(`session.describePath`). `denied` 의 본문은 그 permission 의
   * `core.denied.*` 문장이 그대로 들어오므로 여기에는 **제목만** 있다 —
   * 같은 사실을 두 번 적지 않는다.
   */
  'core.screen.denied': '이 화면을 열 권한이 없습니다',
  'core.screen.denied.notRequestable': '이 권한은 요청으로 받을 수 없습니다.',
  'core.screen.stale': '메뉴가 최신이 아닙니다',
  'core.screen.stale.description': '이 화면을 볼 수 있는데 메뉴에 없습니다. 권한이나 모듈 구독이 방금 바뀌었을 수 있습니다.',
  'core.screen.stale.next': '새로고침',

  /*
   * 화면 단위 에러의 **제목**(`ScreenErrorBoundary`). 아래에 레지스트리 문장이 설명으로
   * 붙으므로 여기서는 같은 말을 반복하지 않는다 — 제목은 "이 화면에 무슨 일이 났나",
   * 설명은 "무엇이 실패했나"다. `RpcFailureKind` 5종과 1:1이고, 종류가 늘면 컴파일이 죽는다.
   */
  'core.screen.error.unauthenticated': '로그인이 풀렸습니다',
  'core.screen.error.forbidden': '이 화면을 열지 못했습니다',
  'core.screen.error.app': '이 화면을 불러오지 못했습니다',
  'core.screen.error.offline': '서버에 닿지 못했습니다',
  'core.screen.error.unknown': '이 화면에서 문제가 생겼습니다',

  /*
   * ── 에러 레지스트리 ─────────────────────────────────────────────────
   * `(status, code)` 는 API 계약이라 바뀌지 않는다. 바뀌는 것은 이 문장들뿐이다.
   * 서버는 렌더된 문장을 모르고 키만 보낸다 — 그래서 웹·모바일·PDF 가 같은 사실을
   * 각자의 문장으로 렌더해도 계약은 하나로 남는다(errors.ts).
   */
  /*
   * 할일 상태 전이 (0038). `(status, code)` 는 계약이라 안 바뀌고(전부 `validation.failed`)
   * 바뀌는 것은 이 문장뿐이다 — 그래서 이유마다 code 를 만들지 않고 `details.messageKey`
   * 로 싣는다(0031 의 그 판단).
   */
  'core.error.todo.sameStatus': '이미 그 상태입니다.',
  'core.error.todo.badTransition': '완료와 취소 사이는 바로 못 갑니다. 먼저 “대기”로 되돌린 다음 바꾸세요.',
  'core.error.todo.noOrgUnit': '소속이 없어 팀 할일을 만들 수 없습니다. 인사 담당자에게 소속 배정을 요청하세요.',
  'core.error.todo.assigneeOutOfScope': '내 조직과 그 아래에 있는 사람에게만 맡길 수 있습니다.',
  'core.error.todo.assigneeNotInService': '재직 중인 사람에게만 맡길 수 있습니다.',
  'core.error.todo.personalAssignee': '개인 할일에는 담당자를 둘 수 없습니다. 남에게 맡기려면 범위를 “팀”으로 바꾸세요.',
  'core.error.todo.notEditor': '이 할 일은 만든 사람 · 담당자 · 그 조직의 장만 고칠 수 있습니다.',
  'core.error.todo.notRemover': '이 할 일은 만든 사람과 그 조직의 장만 지울 수 있습니다.',

  'core.error.auth.unauthenticated': '로그인이 필요합니다',
  'core.error.auth.unauthenticated.next': '다시 로그인하면 하던 곳에서 이어서 진행할 수 있습니다.',
  'core.error.auth.forbidden': '이 작업을 할 권한이 없습니다',
  'core.error.auth.forbidden.next': '회사 관리자에게 필요한 권한을 요청하세요.',
  /*
   * 비밀번호 변경의 세 거절. **"바꾸지 못했습니다" 하나로 접지 않는다** — 셋은 사용자가
   * 할 일이 각각 다르다. `{min}` 은 `MIN_PASSWORD_LENGTH`(contracts)에서 온다:
   * 숫자를 여기 박으면 그것이 정책의 두 번째 원본이 되고, 올리는 날 문장만 옛 수를 말한다.
   */
  'core.error.auth.currentPasswordMismatch': '현재 비밀번호가 맞지 않습니다',
  'core.error.auth.currentPasswordMismatch.next': '지금 쓰고 있는 비밀번호를 다시 입력하면 변경할 수 있습니다.',
  'core.error.auth.passwordTooShort': '새 비밀번호가 너무 짧습니다',
  'core.error.auth.passwordTooShort.next': '{min}자 이상으로 입력하면 변경할 수 있습니다.',
  /*
   * 재설정 링크가 유효하지 않다 — 없음·만료·이미 씀 셋을 **하나로 접은 문장**이다.
   * 셋 다 다음 행동이 같고, 갈라 주면 응답이 "그 토큰이 존재하긴 했다"를 알려 준다.
   */
  'core.error.auth.resetTokenInvalid': '재설정 링크가 유효하지 않거나 이미 사용됐습니다',
  'core.error.auth.resetTokenInvalid.next': '로그인 화면에서 재설정 링크를 다시 요청하세요.',
  /*
   * 이 문장은 **사용자가 스스로 못 고치는 상태**를 말한다(0056). 그래서 다음 행동이
   * "다시 해 보세요"가 아니라 사람에게 가는 것이다 — 자격증명을 정리할 수 있는 문이
   * 아직 제품 안에 없고(CLI 둘뿐), 그 사실이 이 문장의 모양을 정한다.
   */
  'core.error.auth.tooManyCompanies': '이 이메일에 연결된 회사가 너무 많아 로그인할 수 없습니다',
  'core.error.auth.tooManyCompanies.next': '회사 관리자에게 문의하세요. 계정 정리가 필요합니다.',
  'core.error.auth.signupTokenInvalid': '가입 링크가 유효하지 않거나 이미 사용됐습니다',
  'core.error.auth.signupTokenInvalid.next': '회원가입을 처음부터 다시 시작하세요.',
  'core.error.auth.phoneInvalid': '휴대폰 번호 형식이 아닙니다',
  'core.error.auth.phoneInvalid.next': '010으로 시작하는 번호를 숫자만 또는 하이픈과 함께 입력하세요.',
  'core.error.auth.inviteTokenInvalid': '초대 링크가 유효하지 않거나 이미 사용됐습니다',
  'core.error.auth.inviteTokenInvalid.next': '회사 관리자에게 초대를 다시 보내 달라고 요청하세요. 이미 가입했다면 로그인하면 됩니다.',
  'core.error.auth.socialFailed': '소셜 계정 확인을 마치지 못했습니다',
  'core.error.auth.socialFailed.next': '처음 화면으로 돌아가 다시 시도하세요.',
  'core.error.auth.socialEmailMissing': '소셜 계정에서 확인된 이메일을 받지 못했습니다',
  'core.error.auth.socialEmailMissing.next': '이메일 제공에 동의하고 다시 시도하거나, 이메일로 가입하세요.',
  'core.error.auth.socialUnavailable': '이 소셜 로그인은 지금 쓸 수 없습니다',
  'core.error.auth.socialUnavailable.next': '이메일로 로그인하거나 다른 방법을 고르세요.',
  'core.error.auth.socialNoAccount': '이 소셜 계정으로 가입된 회사가 없습니다',
  'core.error.auth.socialNoAccount.next': '회사 관리자에게 초대를 받거나, 웹(worhythm.com)에서 회사를 만든 뒤 다시 로그인하세요.',
  'core.error.auth.socialTaken': '이 소셜 계정은 회사의 다른 사람에게 이미 연결돼 있습니다',
  'core.error.auth.socialTaken.next': '다른 소셜 계정을 쓰거나 회사 관리자에게 문의하세요.',
  'core.error.auth.lastSignInMethod': '마지막 로그인 방법이라 연결을 끊을 수 없습니다',
  'core.error.auth.lastSignInMethod.next': '먼저 비밀번호를 정하거나 다른 소셜 계정을 연결하세요.',
  'core.error.auth.accountLastOwner': '회사를 관리할 사람이 남지 않아 계정을 삭제할 수 없습니다',
  'core.error.auth.accountLastOwner.next': '역할 화면에서 다른 직원에게 오너 역할을 준 뒤 다시 시도하세요.',
  'core.error.people.inviteNoEmail': '이메일이 없는 직원은 초대할 수 없습니다',
  'core.error.people.inviteNoEmail.next': '직원 정보에 이메일을 넣은 뒤 다시 초대하세요.',
  'core.error.people.inviteAlreadyActive': '이미 로그인할 수 있는 직원입니다',
  'core.error.people.inviteAlreadyActive.next': '비밀번호를 잊었다면 로그인 화면의 비밀번호 재설정을 안내하세요.',
  'core.error.auth.passwordUnchanged': '새 비밀번호가 지금 쓰는 것과 같습니다',
  'core.error.auth.passwordUnchanged.next': '다른 비밀번호를 입력하면 변경할 수 있습니다.',
  // 사용자가 할 수 있는 일이 없는 자리(errors.ts 의 noNextAction). 그래서 `.next` 가 없다.
  'core.error.tenant.missingContext': '회사 정보를 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.',
  'core.error.validation.failed': '입력한 내용을 다시 확인해 주세요',
  'core.error.validation.failed.next': '표시된 항목을 고치면 저장됩니다. 입력한 내용은 그대로 남아 있습니다.',
  'core.error.resource.notFound': '찾을 수 없는 화면입니다',
  'core.error.resource.notFound.next': '주소가 바뀌었거나 삭제된 항목일 수 있습니다. 메뉴에서 다시 찾아보세요.',
  'core.error.resource.conflict': '이미 같은 값이 등록되어 있습니다',
  'core.error.resource.conflict.next': '{field} 값을 다른 것으로 바꿔서 다시 저장하세요.',
  'core.error.rbac.lastAdmin': '마지막 권한 관리자는 해제할 수 없습니다',
  'core.error.rbac.lastAdmin.next': '먼저 다른 구성원에게 역할 관리 권한을 부여하세요.',
  'core.error.module.notEnabled': '찾을 수 없는 화면입니다',
  'core.error.module.notEnabled.next': '메뉴에서 다시 찾아보세요.',
  'core.error.rateLimit.exceeded': '요청이 너무 잦습니다',
  'core.error.rateLimit.exceeded.next': '잠시 기다렸다가 다시 시도해 주세요.',
  'core.error.upstream.unavailable': '서버에서 응답을 받지 못했습니다',
  'core.error.upstream.unavailable.next': '네트워크를 확인한 뒤 다시 시도해 주세요.',
  'core.error.internal.unexpected': '문제가 생겨 처리하지 못했습니다',

  /*
   * ── 조직 화면 ────────────────────────────────────────────────────────
   * 이 화면이 **미배정 인원을 먼저 말하는** 이유는 실측이다. 구 시스템의 조직도는 사람을
   * 팀 안에서만 그렸고(팀 없는 직원은 그릴 자리가 없다), 활성 17명 중 7명이 화면에서
   * 사라졌다 — 사라졌다는 문장도 없었다. 그리고 같은 API 의 summary 뷰는 빈 팀까지 세서
   * 두 뷰가 같은 회사에 대해 다른 팀 수를 말했다(3 vs 2).
   */
  'core.screen.org.units.intro': '조직 단위 {units}개 · 배정 {assigned}명',
  'core.screen.org.units.unassigned': '미배정 {count}명',
  'core.screen.org.units.unassigned.hint': '소속이 없는 사람도 회사의 사람입니다. 눌러서 명단을 확인하세요.',
  'core.screen.org.units.unassigned.none': '모두 소속이 있습니다',
  'core.screen.org.units.empty': '아직 조직이 없습니다',
  'core.screen.org.units.empty.next': '첫 조직 단위를 만들면 직원의 소속을 정할 수 있습니다.',
  'core.screen.org.units.col.name': '조직',
  'core.screen.org.units.col.code': '코드',
  'core.screen.org.units.col.manager': '조직장',
  'core.screen.org.units.col.members': '인원',
  'core.screen.org.units.col.actions': '작업',
  'core.screen.org.units.noCode': '없음',
  'core.screen.org.units.noManager': '없음',
  /* 직접 소속과 하위 포함이 다르면 둘 다 말한다. 하나만 그리면 상위 단위가 0명으로 보인다. */
  'core.screen.org.units.members': '{direct}명',
  'core.screen.org.units.members.withSub': '{direct}명 (하위 포함 {total}명)',
  'core.screen.org.units.add': '조직 추가',
  'core.screen.org.units.addChild': '하위 추가',
  'core.screen.org.units.childCount': '하위 조직',
  'core.screen.org.units.edit': '수정',
  'core.screen.org.units.move': '상위 이동',
  'core.screen.org.units.remove': '삭제',
  /* 목록 상한에 걸렸다. **조용히 자르지 않는다** — 잘린 조직도는 "그 부서가 없다"로 읽힌다. */
  'core.screen.org.units.truncated': '조직이 많아 일부만 표시했습니다.',
  'core.screen.org.units.detached': '상위를 찾을 수 없는 단위',
  'core.screen.org.units.detached.hint': '표시 범위 밖에 상위가 있습니다. 숨기지 않고 여기 따로 적습니다.',

  'core.screen.org.units.form.create': '조직 추가',
  'core.screen.org.units.form.create.description': '이름만 있으면 만들어집니다. 코드와 조직장은 나중에 채울 수 있습니다.',
  'core.screen.org.units.form.edit': '{name} 수정',
  'core.screen.org.units.form.edit.description': '이름·코드·순서·조직장을 바꿉니다. 상위 이동은 따로 있습니다.',
  'core.screen.org.units.form.parent': '상위 조직',
  'core.screen.org.units.form.parent.root': '최상위',
  'core.screen.org.units.form.name': '이름',
  'core.screen.org.units.form.code': '코드',
  'core.screen.org.units.form.optional': '(선택)',
  'core.screen.org.units.form.sortOrder': '표시 순서',
  // 힌트는 라벨 **옆에** 붙는다(FieldShell). 문장으로 쓰면 라벨과 이어져 한 덩어리로
  // 읽힌다 — 브라우저에서 실제로 그렇게 보였다(2026-09-03). 짧은 구로 둔다.
  'core.screen.org.units.form.sortOrder.hint': '같은 상위 안에서 작은 수가 앞',
  'core.screen.org.units.form.manager': '조직장',
  'core.screen.org.units.form.manager.none': '지정하지 않음',
  'core.screen.org.units.form.manager.search': '직원 검색',
  'core.screen.org.units.form.manager.search.placeholder': '사번 또는 이름',
  'core.screen.org.units.form.manager.more': '더 있습니다 — 검색으로 좁히세요',
  'core.screen.org.units.form.duplicate': '이미 쓰고 있는 {field}입니다',
  'core.screen.org.units.form.submit': '저장',
  'core.screen.org.units.form.submitting': '저장 중…',
  'core.screen.org.units.form.cancel': '취소',

  'core.screen.org.units.move.title': '{name} 상위 이동',
  'core.screen.org.units.move.description': '이 단위와 그 아래 조직이 통째로 옮겨집니다. 사람의 소속은 그대로입니다.',
  'core.screen.org.units.remove.title': '{name} 삭제',
  'core.screen.org.units.remove.description': '되돌릴 수 없습니다. 하위 조직이나 소속 인원이 남아 있으면 삭제되지 않습니다.',
  'core.screen.org.units.remove.confirm': '삭제',

  /*
   * DB 가 거절한 것을 사람의 말로 옮긴 문장들. 서버는 이 **키**를 싣고 렌더는 화면이 한다
   * (급여 blocker · 근태 계산 오류와 같은 규약) — 그래야 모바일이 같은 사실을 자기 언어로 그린다.
   */
  'core.screen.org.units.error.cycle': '자기 자신이나 자기 하위 조직 밑으로는 옮길 수 없습니다.',
  'core.screen.org.units.error.tooDeep': '조직 단계는 6단까지입니다. 더 얕은 자리로 옮기세요.',
  'core.screen.org.units.error.hasChildren': '하위 조직이 남아 있습니다. 먼저 옮기거나 지우세요.',
  'core.screen.org.units.error.hasMembers': '소속된 사람이 남아 있습니다. 먼저 다른 조직으로 옮기세요.',
  /* 0038. 없어진 조직의 할일은 조직도 어디에도 안 나온다 — 그래서 FK 가 삭제를 막는다. */
  'core.screen.org.units.error.hasTodos': '이 조직에 걸린 할 일이 남아 있습니다. 먼저 지우거나 개인 할일로 옮기세요.',

  /*
   * 부트 실패 화면의 **버튼 라벨.** `internal.unexpected` 에 `.next` 를 지어 붙이지 않는다 —
   * 레지스트리가 그 에러에 대해 "사용자에게 지시할 다음 행동이 없다"(`noNextAction`)고
   * 이미 적었고, 거기에 문장을 하나 만들어 붙이면 레지스트리가 거짓이 된다.
   * 부트에는 다음 행동이 실제로 있다(다시 부른다). 그래서 **부트의 키**로 따로 둔다.
   */
  'core.error.boot.retry': '다시 시도',

  /*
   * 배포 스큐. 에러 레지스트리에 없다 — **응답이 아니라 관찰**이기 때문이다
   * (요청은 성공했고, 지문만 달라졌다). 그래서 `(status, code)` 가 없고 키만 있다.
   */
  'core.error.deploy.skew': '새 버전이 배포되었습니다. 지금 보고 있는 화면이 최신이 아닐 수 있습니다.',
  'core.error.deploy.skew.next': '새로고침',

  /*
   * 자동 작업 화면. **이 화면의 목적은 "돌았다"가 아니라 "안 돌았다"를 말하는 것이다.**
   * 구 시스템의 월차 잡은 두 달간 안 돌았는데 에러도 문장도 없었고, 그동안 사람이
   * 66건을 손으로 메웠다. 그래서 상태 넷이 각각 **다른 행동**을 가리킨다.
   */
  'core.screen.jobs.intro': '정해진 때에 제품이 스스로 도는 작업입니다. 안 돌았으면 여기에 그렇게 적힙니다.',
  'core.screen.jobs.col.job': '작업',
  'core.screen.jobs.col.schedule': '주기',
  'core.screen.jobs.col.last': '마지막 실행',
  'core.screen.jobs.col.health': '상태',
  // 부여일 옵션 이름과 겹치지 않게 **주기**임을 분명히 한다(0018 §8 의 '매월 1일' 사고).
  'core.screen.jobs.schedule.monthly': '매월 {day}일 {time}',
  'core.screen.jobs.schedule.daily': '매일 {time}',
  'core.screen.jobs.schedule.everyMinutes': '{minutes}분마다',
  'core.screen.jobs.health.ok': '정상',
  'core.screen.jobs.health.overdue': '늦음',
  'core.screen.jobs.health.never': '한 번도 안 돎',
  'core.screen.jobs.health.failing': '실패',
  'core.screen.jobs.expected': '예정 {at}',
  'core.screen.jobs.lastNone': '기록 없음',
  'core.screen.jobs.attempt': '{n}번째 시도',
  /*
   * 이 실행이 적힐 때의 결과 모양이 지금 코드와 다르다. 문장을 못 만드므로 **그때 적힌
   * 값 그대로** 보여 준다 — 잡의 결과에 필드가 늘면 그 전 행들이 여기로 온다.
   */
  'core.screen.jobs.result.stale': '옛 형식의 기록 — {raw}',
  'core.screen.jobs.running': '실행 중',
  // 상태마다 **다음 행동**이 다르다. "안 돌았다"에 재시도를 권하면 엉뚱한 곳을 보게 된다.
  'core.screen.jobs.never.next': '이 회사에서 아직 한 번도 실행되지 않았습니다. 워커가 떠 있는지 확인하세요.',
  'core.screen.jobs.overdue.next': '예정 시각이 지났는데 성공 기록이 없습니다.',
  'core.screen.jobs.failing.next': '마지막 실행이 실패했습니다. 사유는 아래에 있습니다.',
  'core.screen.jobs.manual': '직접 실행하는 화면: {screen}',
  'core.screen.jobs.manual.none': '사람이 직접 돌리는 화면이 없는 작업입니다.',
  'core.screen.jobs.tz': '시각은 한국 시간(KST)입니다.',
  'core.screen.jobs.empty': '선언된 자동 작업이 없습니다',
  'core.screen.jobs.empty.next': '이 배포에는 정기 작업이 없습니다.',

  /*
   * 자동 작업 점검(`core.watchdog`)의 이름과 결과. **알림 문구는 여기 없다** —
   * 그건 운영자가 읽고 이 사전은 제품 화면의 것이다(로케일이 늘어도 알림 채널은 하나다).
   */
  'core.job.watchdog.label': '자동 작업 점검',
  'core.job.watchdog.result': '점검 {checked}개 · 늦음 {late}개',

  /*
   * 사용량 관측(`core.usage.snapshot`). 읽는 화면은 아직 없고 이 문장이 그 잡의
   * 유일한 얼굴이다 — 그래서 **관측 수와 삽입 수를 따로** 말한다: `inserted` 가 0 인 것은
   * 실패가 아니라 "이미 적혀 있다"(재시도)이고, 둘을 한 수로 접으면 그 구분이 사라진다.
   * `gapDays` 는 최근 30일 중 관측이 없는 날이다 — 이 표에서 없는 행의 뜻은
   * "그날 잡이 안 돌았다" 하나뿐이라 그 수가 곧 이 잡의 건강이다.
   */
  'core.job.todo.reminder.label': '할일 기한 알림',
  /*
   * ★ `보냄` 과 `대상` 이 다른 수인 것이 이 잡의 요점이다. 구 시스템은 한 할일에
   *   **28건**을 보냈다(중복 판정이 없었다) — 여기서는 이미 보낸 것이 `대상` 에는 있고
   *   `보냄` 에는 없다. 둘을 한 수로 접으면 그 차이를 볼 자리가 사라진다.
   */
  'core.job.todo.reminder.result': '대상 {due}건 · 보냄 {sent}건',
  'core.job.account.purge.label': '계정 삭제',
  'core.job.account.purge.result': '유예 지난 요청 {due}건 · 지움 {purged}건 · 닫힌 회사 {companiesClosed}곳',
  'core.job.usage.snapshot.label': '사용량 관측',
  'core.job.usage.snapshot.result':
    '{day} · 재직 {seats}명 · 입사일 없음 {noJoinDate}명 · 모듈 {modules}개 · '
    + '새로 적은 행 {inserted}개 · 최근 30일 미관측 {gapDays}일',

  /*
   * 알림함. **권한이 없는 화면이라 거부 문구가 없다** — 로그인한 사람은 누구나 연다.
   * 그게 이 화면의 요점이다: 역할 0개인 신규 직원도 자기 휴가 결과를 본다.
   */
  'core.screen.notifications.intro': '나에게 온 알림입니다. 읽으면 표시가 사라집니다.',
  'core.screen.notifications.empty': '아직 온 알림이 없습니다',
  'core.screen.notifications.empty.next': '휴가를 신청하거나 결재를 맡으면 여기에 쌓입니다.',
  'core.screen.notifications.unread': '안 읽음 {count}건',
  'core.screen.notifications.col.action': '작업',
  'core.screen.notifications.markRead': '읽음으로 표시',
  'core.screen.notifications.markAllRead': '보이는 것 모두 읽음',
  'core.screen.notifications.go': '보러 가기',
  'core.screen.notifications.limit': '최근 {count}건만 보여 줍니다.',

  /*
   * 모바일 앱(apps/mobile, 0218)만 쓰는 문장. 화면 문장은 웹 키를 그대로 쓰고, 여기에는 **앱에만
   * 있는 자리**(탭·계정 시트·시간 표기)만 둔다 — 같은 말을 두 키로 적으면 한쪽만 고쳐진다.
   */
  'core.screen.mobile.retry': '다시 시도',
  'core.screen.mobile.cancel': '취소',
  'core.screen.mobile.account': '내 계정',
  'core.screen.mobile.account.name': '이름',
  'core.screen.mobile.account.company': '회사',
  'core.screen.mobile.account.passwordOnWeb': '비밀번호는 웹의 내 계정에서 바꿉니다.',
  'core.screen.mobile.logout': '로그아웃',
  'core.screen.mobile.logout.title': '로그아웃할까요?',
  'core.screen.mobile.logout.description': '이 기기에서만 로그아웃됩니다. 다시 쓰려면 이메일과 비밀번호를 넣어야 합니다.',
  'core.screen.mobile.theme': '화면 밝기',
  'core.screen.mobile.theme.system': '기기 설정',
  'core.screen.mobile.theme.light': '밝게',
  'core.screen.mobile.theme.dark': '어둡게',
  'core.screen.mobile.version': '버전 {version}',
  'core.screen.mobile.won': '{amount}원',
  'core.screen.mobile.minutes': '{minutes}분',
  'core.screen.mobile.hours': '{hours}시간',
  'core.screen.mobile.hoursMinutes': '{hours}시간 {minutes}분',
  // 구독(0219). 가격 숫자는 여기 없다 — 스토어가 준 표시 가격을 그대로 쓴다.
  'core.screen.mobile.billing.title': '구독',
  'core.screen.mobile.billing.headcount': '지금 재직 인원',
  'core.screen.mobile.billing.people': '{count}명',
  'core.screen.mobile.billing.free': '{free}명까지는 무료라 지금은 낼 것이 없습니다.',
  'core.screen.mobile.billing.tier': '최대 {tier}명 구간이 맞습니다.',
  'core.screen.mobile.billing.uncovered': '지금 구독이 인원을 다 덮지 못합니다. 그동안 사용은 막히지 않습니다.',
  'core.screen.mobile.billing.overLargest': '{largest}명을 넘는 회사는 상담으로 정합니다. 운영자에게 문의하세요. 그동안 사용은 막히지 않습니다.',
  'core.screen.mobile.billing.current': '지금 구독',
  'core.screen.mobile.billing.renews': '{date}에 갱신',
  'core.screen.mobile.billing.ends': '{date}에 끝남 (갱신 안 함)',
  'core.screen.mobile.billing.choose': '구독 고르기',
  'core.screen.mobile.billing.plan.all': '근태·급여·전자계약',
  'core.screen.mobile.billing.plan.time': '근태만',
  'core.screen.mobile.billing.period.monthly': '월간',
  'core.screen.mobile.billing.period.yearly': '연간 (10% 할인)',
  'core.screen.mobile.billing.upTo': '최대 {count}명',
  'core.screen.mobile.billing.recommended': '지금 인원에 맞음',
  'core.screen.mobile.billing.buying': '결제하는 중…',
  'core.screen.mobile.billing.howTiers': '{free}명까지는 언제나 무료입니다. 구간은 사장님을 포함한 재직 인원 전부이고, 인원이 늘면 다음 구간으로 바꾸면 됩니다. 차액은 스토어가 정산합니다.',
  'core.screen.mobile.billing.notSelling': '이 앱에서는 아직 구독을 팔지 않습니다',
  'core.screen.mobile.billing.notSelling.next': '스토어 결제가 연결되면 여기서 구독할 수 있습니다. 그동안 사용은 막히지 않습니다.',
  'core.screen.mobile.billing.noProducts': '스토어에서 상품을 찾지 못했습니다',
  'core.screen.mobile.billing.noProducts.next': '잠시 뒤 다시 시도하세요. 계속 비어 있으면 운영자에게 알려 주세요.',
  'core.screen.mobile.billing.restore': '구매 복원',
  'core.screen.mobile.billing.manage': '구독 관리 · 해지',
  'core.screen.mobile.billing.terms': '이용약관',
  'core.screen.mobile.billing.privacy': '개인정보 처리방침',

  /*
   * 할일. 알림함·내 계정과 **같은 종류의 화면**이다(`kind: 'authenticated'`) — 권한 0개인
   * 신규 직원이야말로 첫날부터 자기 할 일을 적는다.
   *
   * ★ 팀 범위를 못 고르는 사람이 있다(소속 미배정). 그 사실을 **빈 드롭다운이 아니라
   *   문장으로** 말한다 — 0015 의 "조직도에서 사라진 7명"과 같은 자리이고, 0018 이 폼의
   *   안내문에서 밟은 자리이기도 하다(폼이 거절하는 이유를 안내문이 부정하면 안 된다).
   */
  'core.screen.todos.intro': '내가 적은 일과 내 조직에 걸린 일입니다. 팀 할일은 그 조직과 그 아래 전부가 봅니다.',
  /* ★ '진행' 이라고 쓰면 상태 라벨 '진행 중' 과 겹쳐서, 대기 중인 할일 하나가 "진행 1" 로
     읽힌다(브라우저에서 봤다 — 0038). 이 수는 `pending + in_progress` 이므로 '남음' 이다. */
  'core.screen.todos.summary': '전체 {total} · 남음 {open} · 완료 {completed}',
  'core.screen.todos.summary.due': '오늘 마감 {dueToday} · 이번 주 {dueThisWeek} · 기한 지남 {overdue}',
  'core.screen.todos.empty': '아직 적은 할 일이 없습니다',
  'core.screen.todos.empty.next': '오른쪽 위 “할 일 추가”로 하나 적어 두면 기한 하루 전에 알려 드립니다.',
  'core.screen.todos.empty.filtered': '이 조건에 맞는 할 일이 없습니다',
  'core.screen.todos.empty.filtered.next': '필터를 “전체”로 되돌리면 나머지가 보입니다.',
  'core.screen.todos.add': '할 일 추가',
  'core.screen.todos.col.title': '할 일',
  'core.screen.todos.col.due': '기한',
  'core.screen.todos.col.owner': '담당',
  'core.screen.todos.col.status': '상태',
  'core.screen.todos.col.action': '작업',
  'core.screen.todos.edit': '고치기',
  'core.screen.todos.remove': '삭제',
  'core.screen.todos.noDue': '기한 없음',
  'core.screen.todos.overdue': '기한 지남',
  'core.screen.todos.dueToday': '오늘 마감',
  'core.screen.todos.unassigned': '담당 없음',
  'core.screen.todos.noUnit': '소속이 없어 팀 할일을 만들 수 없습니다. 인사 담당자에게 소속 배정을 요청하세요.',
  'core.screen.todos.assigneesTruncated': '담당자 후보를 {count}명까지만 보여 줍니다. 더 아래 조직의 사람은 그 조직에서 맡기세요.',
  'core.screen.todos.limit': '최근 {count}건만 보여 줍니다. 필터로 좁히면 나머지가 보입니다.',

  /* 필터. '전체'가 기본이고, 그것이 요약과 목록이 다른 수를 말하지 않는 유일한 상태다. */
  'core.screen.todos.filter.all': '전체',
  'core.screen.todos.filter.status': '상태',
  'core.screen.todos.filter.scope': '범위',
  'core.screen.todos.filter.assignedToMe': '내가 담당',
  'core.screen.todos.filter.due': '기한',
  'core.screen.todos.filter.due.week': '이번 주',
  'core.screen.todos.filter.due.month': '이번 달',

  /* 범위 둘. `personal` 은 나만 보고 `team` 은 그 조직과 그 아래 전부가 본다. */
  'core.screen.todos.scope.personal': '개인',
  'core.screen.todos.scope.team': '팀',

  /* 상태 넷. 값 집합은 contracts 가 소유하고 이 넷이 그 사전이다. */
  /* ★ 키에 밑줄을 쓰지 않는다 — i18n 규약의 `REST_RE` 가 kebab/camel 만 받는다.
     값은 `in_progress` 이고 키는 `inProgress` 다: 값 집합은 DB 의 것이고 키는 사전의 것이다. */
  'core.screen.todos.status.pending': '대기',
  'core.screen.todos.status.inProgress': '진행 중',
  'core.screen.todos.status.completed': '완료',
  'core.screen.todos.status.cancelled': '취소',

  /* 우선순위 넷. 선언 순서가 곧 목록의 정렬 순서다(`TODO_PRIORITIES`). */
  'core.screen.todos.priority.urgent': '긴급',
  'core.screen.todos.priority.high': '높음',
  'core.screen.todos.priority.medium': '보통',
  'core.screen.todos.priority.low': '낮음',

  /* 만들기·고치기 다이얼로그. */
  'core.screen.todos.form.create': '할 일 추가',
  'core.screen.todos.form.edit': '할 일 고치기',
  'core.screen.todos.form.title': '할 일',
  'core.screen.todos.form.description': '설명',
  'core.screen.todos.form.due': '기한',
  'core.screen.todos.form.priority': '우선순위',
  'core.screen.todos.form.scope': '범위',
  'core.screen.todos.form.status': '상태',
  'core.screen.todos.form.assignee': '담당자',
  'core.screen.todos.form.assignee.none': '지정하지 않음',
  'core.screen.todos.form.assignee.hint': '담당자를 정하면 그 사람에게 알림이 갑니다.',
  /* ★ `{...}` 뒤에 조사를 붙이지 않는다 — 받침을 정하는 낱말이 사용자 데이터다(AGENTS.md 30).
     "개발본부 과(와)" 가 브라우저에 그대로 떴다. 가운뎃점이 그 자리를 대신한다. */
  'core.screen.todos.form.teamHint': '{unit} · 그 아래 조직까지 전부 봅니다.',
  'core.screen.todos.form.submit': '저장',
  'core.screen.todos.form.cancel': '닫기',

  /* 삭제. 되돌릴 수 없으므로 확인을 받는다(AGENTS.md 14 의 파괴적 동작). */
  'core.screen.todos.removeDialog.title': '이 할 일을 지울까요?',
  /* 같은 이유로 조사가 없다. 제목을 따옴표 안에 두고 문장은 그 뒤에서 시작한다. */
  'core.screen.todos.removeDialog.body': '“{title}” — 이 할 일을 지웁니다. 되돌릴 수 없습니다.',
  'core.screen.todos.removeDialog.confirm': '지운다',

  /*
   * 알림 문장. **행에는 `(kind, params)` 만 있다** — 한국어를 저장하면 로케일이 둘이
   * 되는 날 지난 알림만 번역되지 않는다(0022).
   */
  'core.notice.todo.assigned': '{name} 님이 할 일을 맡겼습니다 · {title}',
  'core.notice.todo.dueTomorrow': '내일 마감입니다 · {title}',
  'core.notice.todo.dueToday': '오늘 마감입니다 · {title}',
  'core.notice.todo.overdue': '기한이 지났습니다 · {title} · 마감 {dueDate}',
  /*
   * 계좌 변경 알림(0052). **params 가 없다** — 은행 이름도 계좌 조각도 안 싣는다.
   * 사람이 "내가 방금 했다"를 판정하는 재료는 알림의 시각 하나로 충분하고, 알림 행은
   * 계좌를 보관하는 표가 아니다(규칙 52 의 축).
   */
  'core.notice.people.bank.changed': '급여 이체 계좌가 등록·변경되었습니다. 본인이 하지 않았다면 인사 담당자에게 바로 알려 주세요.',
  'core.notice.people.bank.removed': '급여 이체 계좌가 삭제되었습니다. 등록하지 않으면 다음 급여를 이체받을 수 없습니다.',

  /*
   * 내 계정. 알림함과 같은 종류의 화면이다(`kind: 'authenticated'`) — 권한 0개인 사람도
   * 자기 비밀번호는 바꿀 수 있어야 하고, permission 을 걸면 그 사람에게 영원히 안 보인다.
   *
   * ★ 안내문이 폼이 실제로 요구하는 것과 같은지 매번 본다. 입사일을 필수로 만들고
   *   "사번과 이름만 있으면 등록됩니다"를 그대로 뒀던 자리가 있다(0018) — 폼이 거절하는
   *   이유를 안내문이 부정하면 사용자에게 그것은 고장 난 버튼이다.
   */
  'core.screen.account.intro': '로그인에 쓰는 정보입니다. 비밀번호를 바꾸면 다른 기기에서 로그인한 것이 모두 끊깁니다.',
  'core.screen.account.email': '로그인 이메일',
  'core.screen.account.password': '비밀번호 변경',
  'core.screen.account.password.current': '현재 비밀번호',
  'core.screen.account.password.new': '새 비밀번호',
  'core.screen.account.password.confirm': '새 비밀번호 확인',
  /*
   * `{min}` 의 원본은 `MIN_PASSWORD_LENGTH` 다. 화면이 그 값을 넘긴다 — 여기 숫자를 적지 않는다.
   *
   * ★ 둘로 나눠져 있다. `Field.hint` 는 **라벨 안**에 들어가므로(FieldShell) 그 문장이
   *   곧 그 칸의 접근 가능한 이름의 일부가 된다 — 긴 설명을 거기 넣으면 스크린리더가
   *   칸 이름으로 두 줄을 읽는다. 그래서 칸 옆에는 규칙만, 왜인지는 폼 위의 한 줄이다.
   */
  'core.screen.account.password.rule': '({min}자 이상)',
  'core.screen.account.password.rule.why': '대문자·특수문자는 요구하지 않습니다. 짧고 복잡한 것보다 긴 쪽이 안전합니다.',
  'core.screen.account.password.tooShort': '{min}자 이상으로 입력해 주세요.',
  'core.screen.account.password.mismatch': '위에 입력한 새 비밀번호와 다릅니다.',
  'core.screen.account.password.unchanged': '지금 쓰는 비밀번호와 같습니다.',
  // 소셜 연결(0225).
  'core.screen.account.social': '소셜 로그인',
  'core.screen.account.social.why': '연결해 두면 비밀번호 없이 그 계정으로 로그인할 수 있습니다.',
  'core.screen.account.social.loading': '연결을 불러오는 중…',
  'core.screen.account.social.none': '연결된 소셜 계정이 없습니다.',
  'core.screen.account.social.unlink': '연결 해제',
  'core.screen.account.social.unlinking': '해제하는 중…',
  'core.screen.account.social.noPassword': '이 계정에는 아직 비밀번호가 없습니다. 비밀번호로도 로그인하려면 로그아웃한 뒤 로그인 화면의 “비밀번호를 잊으셨나요?”로 정하세요.',
  'core.screen.account.submit': '비밀번호 바꾸기',
  'core.screen.account.submitting': '바꾸는 중…',
  /* 성공은 두 문장이다 — 바뀌었다는 사실과, **다른 기기가 몇 개 끊겼는가.** 0 도 사실이다. */
  'core.screen.account.done': '비밀번호를 바꿨습니다.',
  'core.screen.account.done.sessions': '다른 기기에서 로그인한 것 {count}개가 끊겼습니다.',
  'core.screen.account.done.noSessions': '다른 기기에서 로그인한 것은 없었습니다.',
  /*
   * 비밀번호를 잊은 경우. ★ 2026-09-08(0036)에 이 문장도 바뀌었다 — 그전에는 "이메일로
   * 스스로 재설정하는 기능은 아직 없습니다"였고, 그것이 그날 거짓이 됐다.
   * 로그아웃한 뒤에야 쓸 수 있는 경로라(재설정 화면은 부트 프레임의 상태다) 그 사실을 적는다.
   */
  /* ── 내 정보 (0052) ─────────────────────────────────────────────────────── */
  'core.screen.my.profile.intro': '회사가 나에 대해 알고 있는 정보입니다. 연락처·사진·급여 이체 계좌는 본인이 직접 고칠 수 있습니다.',
  'core.screen.my.profile.section.company': '회사가 정하는 정보',
  'core.screen.my.profile.section.contact': '연락처',
  'core.screen.my.profile.section.bank': '급여 이체 계좌',
  'core.screen.my.profile.employeeNo': '사번',
  'core.screen.my.profile.name': '이름',
  'core.screen.my.profile.org': '소속',
  'core.screen.my.profile.noOrg': '미배정',
  'core.screen.my.profile.joinedOn': '입사일',
  'core.screen.my.profile.noJoinedOn': '등록되지 않음',
  'core.screen.my.profile.leftOn': '퇴사일',
  'core.screen.my.profile.company.why': '이 칸들은 회사가 정하는 기록이라 여기서 고칠 수 없습니다. 틀린 곳이 있으면 인사 담당자에게 알려 주세요.',
  'core.screen.my.profile.email': '이메일',
  'core.screen.my.profile.email.hint': '급여명세서는 아래 이메일 주소로 갑니다.',
  'core.screen.my.profile.email.taken': '회사 안의 다른 사람이 이미 쓰는 이메일입니다.',
  'core.screen.my.profile.email.invalid': '이메일 형식이 아닙니다.',
  'core.screen.my.profile.loginEmail': '로그인 아이디',
  'core.screen.my.profile.loginEmail.why': '로그인에 쓰는 주소는 위 이메일과 별개이고 여기서 바뀌지 않습니다. 바꾸려면 인사 담당자에게 요청하세요.',
  'core.screen.my.profile.loginEmail.none': '아직 로그인 계정이 없습니다',
  'core.screen.my.profile.phone': '전화번호',
  'core.screen.my.profile.save': '연락처 저장',
  'core.screen.my.profile.saving': '저장하는 중…',
  'core.screen.my.profile.saved': '연락처를 저장했습니다.',
  'core.screen.my.profile.photo': '프로필 사진',
  'core.screen.my.profile.photo.change': '사진 올리기',
  'core.screen.my.profile.photo.uploading': '올리는 중…',
  'core.screen.my.profile.photo.remove': '사진 지우기',
  'core.screen.my.profile.photo.remove.confirm': '사진을 지웁니다. 되돌릴 수 없습니다.',
  'core.screen.my.profile.photo.none': '아직 사진이 없습니다',
  'core.screen.my.profile.photo.hint': '2MB 이하의 JPG·PNG·WebP 파일을 올릴 수 있습니다.',
  'core.screen.my.profile.photo.tooLarge': '2MB 이하의 사진만 올릴 수 있습니다.',
  'core.screen.my.profile.photo.badType': 'JPG·PNG·WebP 형식만 올릴 수 있습니다.',
  'core.screen.my.profile.photo.failed': '사진을 올리지 못했습니다. 잠시 뒤 다시 시도해 주세요.',
  'core.screen.my.profile.bank.intro': '급여가 이 계좌로 나갑니다. 계좌가 바뀌면 알림이 한 건 남습니다 — 본인이 하지 않은 변경을 알아차리는 자리입니다.',
  'core.screen.my.profile.bank.none': '아직 등록하지 않았습니다. 등록하지 않으면 급여를 이체받을 수 없습니다.',
  'core.screen.my.profile.bank.dialog.intro': '저장하면 내 알림함에 변경 기록이 한 건 남습니다.',
  'core.screen.my.profile.bank.updatedAt': '마지막 수정',
  'core.screen.my.profile.bank.edit': '계좌 등록·수정',
  'core.screen.my.profile.bank.remove': '계좌 지우기',
  'core.screen.my.profile.bank.remove.confirm': '계좌를 지웁니다. 다시 등록하기 전까지 급여를 이체받을 수 없습니다.',
  'core.screen.my.profile.bank.cancel': '닫기',
  'core.screen.my.profile.bank.save': '저장',
  'core.screen.my.profile.bank.saving': '저장하는 중…',

  'core.screen.account.forgot': '비밀번호가 기억나지 않으면 로그아웃한 뒤 로그인 화면의 “비밀번호를 잊으셨나요?”로 재설정할 수 있습니다.',

  /*
   * 급여 이체 계좌 화면(0037). 이 화면이 답하는 질문은 "이 사람의 계좌가 뭐냐"가 아니라
   * **"누가 아직 이체를 못 받나"** 다 — 그래서 미등록이 빈 칸이 아니라 **말**이다(0032).
   */
  'core.screen.people.bank.intro': '급여가 나가는 계좌입니다. 등록되지 않은 사람은 이체를 받지 못합니다.',
  'core.screen.people.bank.missing': '계좌 미등록 {count}명',
  'core.screen.people.bank.missing.none': '재직자 전원이 계좌를 등록했습니다.',
  'core.screen.people.bank.employee': '직원',
  'core.screen.people.bank.bank': '은행',
  'core.screen.people.bank.number': '계좌번호',
  'core.screen.people.bank.updatedAt': '마지막 수정',
  'core.screen.people.bank.none': '미등록',
  'core.screen.people.bank.left': '퇴사',
  /* 퇴사자는 목록에 남지만 미등록 수에는 안 센다 — 이체를 받을 사람이 아니다. */
  'core.screen.people.bank.showLeft': '퇴사자도 보기',
  'core.screen.people.bank.empty': '아직 직원이 없습니다',
  'core.screen.people.bank.edit': '계좌 등록·수정',
  'core.screen.people.bank.remove': '계좌 지우기',
  'core.screen.people.bank.remove.confirm': '이 사람의 계좌를 지웁니다. 다음 급여부터 이체 대상에서 빠집니다.',
  'core.screen.people.bank.save': '저장',
  'core.screen.people.bank.saving': '저장하는 중…',
  'core.screen.people.bank.cancel': '취소',
  'core.screen.people.bank.bank.select': '은행을 고르세요',
  /*
   * ★ 사람은 통장에 적힌 대로 하이픈을 넣어 친다. 저장은 숫자만이고, 그 사실을 미리 말한다.
   *
   * **둘로 나눠져 있다**(0024 의 그 자리, 2026-09-08 에 브라우저에서 다시 밟았다):
   * `Field.hint` 는 라벨 **안**에 들어가므로(FieldShell) 그 문장이 곧 칸의 접근 가능한
   * 이름의 일부가 된다 — 긴 설명을 거기 넣으면 스크린리더가 칸 이름으로 두 줄을 읽고,
   * 화면에서도 라벨이 한 문단이 된다. 그래서 칸 옆에는 **규칙만**, 왜인지는 폼 위의 한 줄이다.
   */
  'core.screen.people.bank.number.hint': '(숫자만 저장)',
  'core.screen.people.bank.number.why': '통장에 적힌 대로 하이픈을 넣어 쳐도 됩니다. 저장할 때 숫자만 남깁니다.',
  'core.screen.people.bank.number.invalid': '계좌번호는 숫자 {min}~{max}자리입니다.',
  'core.screen.people.bank.bank.required': '은행을 골라 주세요.',

  /*
   * ── 주민등록번호 (0051) ─────────────────────────────────────────────────
   * ★ 이 묶음에서 가장 중요한 문장은 **`intro` 와 `masked.why`** 다: 화면이 전체 번호를
   * 안 보여 준다는 사실을 사람이 먼저 알아야, 마스킹을 "안 불러와졌다"로 읽지 않는다.
   *
   * 거절 문장이 셋인 이유는 **사람이 할 일이 셋 다 다르기 때문**이다(0034 의 `ipUnknown`
   * 과 같은 자리): 자릿수는 다시 세는 것이고, 날짜는 앞 6자리를 고치는 것이고,
   * 성별 자리는 7번째 한 글자다. "형식이 틀렸습니다" 하나로는 어디를 고칠지 모른다.
   */
  'core.screen.people.identity.intro': '계약서에 찍히는 주민등록번호입니다. 저장할 때 암호화되고, 이 화면에는 앞 7자리만 보입니다.',
  'core.screen.people.identity.missing': '미등록 {count}명',
  'core.screen.people.identity.missing.none': '재직자 전원이 등록돼 있습니다.',
  'core.screen.people.identity.employee': '직원',
  'core.screen.people.identity.masked': '주민등록번호',
  'core.screen.people.identity.masked.why': '전체 번호는 화면에 나오지 않습니다. 계약서를 보낼 때 서버가 문서에만 채웁니다.',
  'core.screen.people.identity.birthDate': '생년월일',
  'core.screen.people.identity.updatedAt': '마지막 수정',
  'core.screen.people.identity.none': '미등록',
  'core.screen.people.identity.left': '퇴사',
  'core.screen.people.identity.showLeft': '퇴사자도 보기',
  'core.screen.people.identity.empty': '아직 직원이 없습니다',
  'core.screen.people.identity.edit': '등록·수정',
  'core.screen.people.identity.remove': '지우기',
  'core.screen.people.identity.remove.confirm': '이 사람의 주민등록번호를 지웁니다. 계약서의 그 칸은 비워집니다.',
  'core.screen.people.identity.save': '저장',
  'core.screen.people.identity.saving': '저장하는 중…',
  'core.screen.people.identity.cancel': '취소',
  'core.screen.people.identity.number': '주민등록번호',
  'core.screen.people.identity.number.hint': '(숫자 13자리)',
  'core.screen.people.identity.number.why': '하이픈을 넣어 쳐도 됩니다. 저장할 때 숫자만 남기고 암호화합니다. 저장한 뒤에는 다시 볼 수 없습니다.',
  'core.screen.people.identity.invalid.length': '숫자 13자리여야 합니다. 앞 6자리와 뒤 7자리를 다시 세어 보세요.',
  'core.screen.people.identity.invalid.birthDate': '앞 6자리가 실재하는 날짜가 아닙니다.',
  'core.screen.people.identity.invalid.genderDigit': '7번째 자리는 1~8 이어야 합니다.',
  'core.screen.people.identity.noKey': '이 서버에 암호화 키가 없어 저장할 수 없습니다. 운영자에게 WORHYTHM_PII_KEY 설정을 요청하세요.',
  'core.screen.people.identity.noKey.banner': '암호화 키가 없는 서버입니다. 지금은 등록·수정이 거절됩니다.',

  /*
   * 이메일 화면(0036). 목록의 원본은 **코드의 선언**이라, 한 번도 고치지 않은 템플릿도
   * 반드시 한 줄로 나온다(자동 작업 화면과 같은 자리).
   */
  'core.screen.email.intro': '회사가 보내는 메일의 문구입니다. 고치지 않으면 기본 문구가 나갑니다.',
  'core.screen.email.transport': '발송 통로',
  'core.screen.email.from': '보내는 주소',
  /*
   * ★ 이 서버가 실제로는 메일을 안 보내는 상태(`log` 어댑터)를 **화면이 말한다.**
   * 안 말하면 사용자는 보낸 줄 안다 — 0034 의 `ipUnknown` 과 같은 자리이고,
   * 배포 설정이라 DB 로는 표현할 수 없다.
   */
  'core.screen.email.transport.log': '이 서버는 메일을 실제로 보내지 않습니다(개발용). 이력에는 남습니다.',
  'core.screen.email.template.default': '기본 문구',
  'core.screen.email.template.custom': '고친 문구',
  'core.screen.email.template.updatedAt': '마지막 수정',
  'core.screen.email.subject': '제목',
  'core.screen.email.body': '본문',
  'core.screen.email.variables': '쓸 수 있는 변수',
  'core.screen.email.variables.required': '필수',
  'core.screen.email.variables.hint': '본문에 {{변수이름}} 형태로 넣으면 보낼 때 값이 채워집니다.',
  'core.screen.email.edit': '문구 고치기',
  'core.screen.email.save': '저장',
  'core.screen.email.saving': '저장하는 중…',
  'core.screen.email.cancel': '취소',
  'core.screen.email.preview': '미리보기',
  'core.screen.email.preview.title': '미리보기',
  'core.screen.email.preview.hint': '변수 자리에는 예시 값이 들어갑니다.',
  'core.screen.email.reset': '기본 문구로 되돌리기',
  'core.screen.email.reset.confirm': '고친 문구를 지우고 기본 문구로 되돌립니다. 되돌린 뒤에는 기본 문구가 바뀌면 그대로 따라갑니다.',
  'core.screen.email.sendTest': '테스트 발송',
  'core.screen.email.sendTest.hint': '내 로그인 이메일로만 보냅니다.',
  /* ★ `{to}` 뒤에 조사를 안 붙인다(30번). 받침이 이메일 주소에 딸려 있다. */
  'core.screen.email.sendTest.done': '테스트 메일을 보냈습니다. 받는 사람: {to}',
  'core.screen.email.sendTest.failed': '보내지 못했습니다: {error}',
  /* 본문이 이력에 남지 않는 템플릿. **보낸 뒤에 알면 늦으므로 화면이 미리 말한다.** */
  // 템플릿마다 다른 링크가 들어간다(재설정 링크 · 명세서 다운로드 링크). 종류를 적으면
  // 템플릿이 늘 때마다 이 문장이 거짓이 된다 — 2026-09-10 에 실제로 그랬다(0044).
  'core.screen.email.body.redacted': '이 메일의 본문은 이력에 남지 않습니다 — 본문에 링크(=토큰)가 들어가기 때문입니다.',
  'core.screen.email.history': '발송 이력',
  'core.screen.email.history.empty': '아직 보낸 메일이 없습니다',
  'core.screen.email.history.to': '받는 사람',
  'core.screen.email.history.status': '결과',
  'core.screen.email.history.status.sent': '보냄',
  'core.screen.email.history.status.failed': '실패',
  'core.screen.email.history.sentAt': '보낸 시각',
  'core.screen.email.history.bodyRedacted': '본문은 남기지 않았습니다',
  'core.screen.email.history.limit': '최근 {limit}건만 보여 줍니다.',
  /* 이력 권한이 없는 사람. **빈 목록으로 접지 않는다** — 그건 "보낸 적이 없다"와 같은 화면이다(0028). */
  'core.screen.email.history.denied': '발송 이력을 볼 권한이 없습니다.',
  /*
   * 템플릿 저장 거절. **`{name}` 뒤에 조사를 붙이지 않는다**(30번 규칙) —
   * 받침이 변수 값에 따라 달라지고, 그 값은 사람이 친 변수 이름이다.
   */
  'core.screen.email.error.unknownVariable': '이 템플릿에 없는 변수입니다: {name}',
  'core.screen.email.error.missingRequired': '반드시 들어가야 하는 변수가 빠졌습니다: {name}',
  'core.screen.email.error.emptySubject': '제목이 비어 있습니다.',
  'core.screen.email.error.emptyBody': '본문이 비어 있습니다.',

  /* 이메일 템플릿의 이름·설명과 변수 이름. 화면이 `t(변수)` 로 부르므로 조립 순회에 실린다. */
  'core.screen.email.template.password-reset': '비밀번호 재설정 안내',
  'core.screen.email.template.password-reset.desc': '비밀번호를 잊은 사람이 재설정 링크를 요청하면 나가는 메일입니다.',
  'core.screen.email.var.name': '받는 사람 이름',
  'core.screen.email.var.company': '회사 이름',
  'core.screen.email.var.resetUrl': '재설정 링크',
  'core.screen.email.var.expiresInHours': '링크 유효 시간(시)',
  'core.screen.email.template.invitation': '직원 초대',
  'core.screen.email.template.invitation.desc': '직원을 초대하면 나가는 메일입니다. 받은 사람이 링크에서 로그인 방법을 정합니다.',
  'core.screen.email.invitation.var.name': '초대받는 사람 이름',
  'core.screen.email.invitation.var.company': '회사 이름',
  'core.screen.email.invitation.var.inviterName': '초대한 사람 이름',
  'core.screen.email.invitation.var.inviteUrl': '초대 링크',
  'core.screen.email.invitation.var.expiresInDays': '링크 유효 기간(일)',
  /*
   * ══ 회사 정보 (0039) ═══════════════════════════════════════════════════════
   *
   * 이 화면의 문장이 답해야 하는 것은 "왜 이걸 적나"다 — 사업자등록번호를 묻는 폼은
   * 이유를 안 적으면 사람이 왜 필요한지 모르고, 그 상태에서 빈 칸을 남긴다.
   * 그리고 그 빈 칸의 대가는 나중에 **명세서에 회사 정보가 비는 것**이다.
   */
  'core.screen.company.intro': '급여명세서·퇴직정산서·계약서에 찍히는 회사 정보입니다. 서류를 만들기 전에 채워 두세요.',
  'core.screen.company.section': '서류에 들어가는 정보',
  /* 화면에 뜨는 이름과 상호는 **다른 사실이다**(contracts 의 그 주석). 그 구분을 문장이 든다. */
  'core.screen.company.tenantName': '이 회사',
  'core.screen.company.tenantName.why': '화면과 내비에 뜨는 이름입니다. 바꾸려면 관리자에게 문의하세요 — 서류에 찍히는 이름은 아래 “상호”이고, 사업자등록증에 적힌 이름을 넣습니다.',
  'core.screen.company.legalName': '상호',
  'core.screen.company.number': '사업자등록번호',
  'core.screen.company.number.hint': '(숫자만 저장)',
  'core.screen.company.number.why': '하이픈을 넣어 쳐도 됩니다. 저장할 때 숫자만 남기고, 서류에는 123-45-67890 모양으로 찍힙니다.',
  'core.screen.company.number.length': '사업자등록번호는 숫자 {digits}자리입니다.',
  /* ★ 자릿수 오류와 **다른 문장**이다 — 열 자리를 채운 사람에게 "10자리를 넣으세요"는 고칠 수 없는 말이다. */
  'core.screen.company.number.invalid': '사업자등록번호 검증 규칙을 통과하지 못했습니다. 자리를 잘못 옮겨 적었는지 확인해 주세요.',
  'core.screen.company.representative': '대표자',
  'core.screen.company.address': '주소',
  'core.screen.company.phone': '대표 전화',
  'core.screen.company.phone.hint': '(적은 그대로 저장)',
  // 최저시급(0042). 코드에 연도표를 두지 않았으므로 갱신은 사람의 일이고, 그 사실을 말한다.
  'core.screen.company.minimumHourlyWage': '최저시급 (원)',
  'core.screen.company.minimumHourlyWage.hint': '계약서 서식에 인쇄됩니다. 매년 고시되면 여기서 갱신하세요.',
  'core.screen.company.email': '대표 이메일',
  'core.screen.company.email.invalid': '이메일 형식이 아닙니다.',
  'core.screen.company.save': '저장',
  'core.screen.company.saving': '저장하는 중…',
  'core.screen.company.saved': '회사 정보를 저장했습니다.',
  /* 아직 아무것도 저장되지 않은 상태. **빈 폼과 다른 사실이라 말한다**(0037 의 "미등록"). */
  'core.screen.company.never': '아직 회사 정보를 적지 않았습니다.',
  'core.screen.company.updatedAt': '마지막 수정 {at}',
  'core.screen.company.failed': '회사 정보를 불러오지 못했습니다',
  /*
   * ══ 업데이트 공지 (0040) ═══════════════════════════════════════════════════
   *
   * 목록은 코드의 선언에서 온다 — 여기 있는 것은 **그 목록을 감싸는 문장**뿐이다.
   * 공지의 제목·설명은 사람이 그때그때 쓰는 글이라 사전에 넣지 않는다(테넌트 데이터가
   * 아니라 **제품의 글**이고, 로케일이 둘이 되는 날 그 파일에 언어를 더한다).
   */
  'core.screen.releases.intro': '이 제품이 무엇을 바꿨는지 적어 둔 자리입니다. 최신이 맨 위입니다.',
  'core.screen.releases.unread': '안 읽은 공지 {count}건',
  'core.screen.releases.unread.none': '새 공지를 모두 읽었습니다.',
  'core.screen.releases.markAll': '모두 읽음',
  'core.screen.releases.marking': '표시하는 중…',
  'core.screen.releases.new': '새 공지',
  'core.screen.releases.empty': '아직 공지가 없습니다',
  'core.screen.releases.failed': '업데이트 공지를 불러오지 못했습니다',
  /* 변경의 종류 — 닫힌 집합이고 파생 키를 만들지 않는다(i18n 게이트의 소스 스캔이 못 본다). */
  'core.screen.releases.kind.feat': '새 기능',
  'core.screen.releases.kind.enh': '개선',
  'core.screen.releases.kind.fix': '수정',

  /* ── 직원 명부 내보내기·가져오기 (0043) ─────────────────────────────────────
   *
   * ★ **내보내기의 열 제목이 곧 가져오기의 머리글이다.** 가져오기가 열을 위치가 아니라
   *   이 문장으로 찾으므로, 여기를 고치면 **예전에 내보낸 파일이 안 들어간다** —
   *   고칠 때는 그 사실을 알고 고쳐야 한다(그래서 이 주석이 여기 있다).
   */
  'core.export.column.employeeNo': '사번',
  'core.export.column.name': '이름',
  'core.export.column.email': '이메일',
  'core.export.column.phone': '연락처',
  'core.export.column.orgUnit': '소속',
  'core.export.column.joinedOn': '입사일',
  'core.export.column.leftOn': '퇴사일',
  'core.export.column.socialInsurance': '4대보험',
  'core.export.value.insured': '가입',
  'core.export.value.uninsured': '미가입',
  'core.export.roster.title': '직원명부',

  // 가져오기 — 파일 자체를 못 읽은 것(위 넷)과 줄 하나가 거절된 것(아래)은 다른 사실이다.
  'core.error.import.notXlsx': '엑셀 파일(.xlsx)이 아니거나 파일이 깨졌습니다. 명부를 내려받아 그 파일을 고쳐서 올리세요.',
  'core.error.import.noSheet': '시트가 없는 파일입니다.',
  'core.error.import.missingColumns': '{columns} 열이 없습니다. 명부를 내려받아 그 파일을 고쳐서 올리세요.',
  'core.error.import.employeeNoRequired': '사번이 비어 있습니다.',
  'core.error.import.nameRequired': '이름이 비어 있습니다.',
  'core.error.import.badDate': '날짜 형식이 아닙니다. 2026-09-01 처럼 적으세요.',
  'core.error.import.unknownOrgUnit': '“{name}” 이라는 소속이 없습니다. 조직 화면에서 먼저 만드세요.',
  'core.error.import.badInsurance': '4대보험 칸은 “{insured}” 또는 “{uninsured}” 여야 합니다.',
  'core.error.import.duplicateEmail': '같은 이메일을 쓰는 직원이 이미 있습니다.',
  'core.error.import.leftBeforeJoined': '퇴사일이 입사일보다 빠릅니다.',
  'core.error.import.rowRejected': '이 줄을 저장하지 못했습니다.',

  // 화면
  'core.screen.people.export': '명부 엑셀',
  'core.screen.people.import': '엑셀로 가져오기',
  'core.screen.people.import.title': '직원 명부를 엑셀로 가져옵니다',
  'core.screen.people.import.description': '사번이 같으면 덮어씁니다. 되돌릴 수 없으니 명부를 먼저 내려받아 그 파일을 고쳐서 올리세요.',
  'core.screen.people.import.pick': '파일 고르기',
  'core.screen.people.import.uploading': '올리는 중…',
  'core.screen.people.import.result': '{total}줄 중 {created}명 추가 · {updated}명 수정 · {failed}줄 실패',
  'core.screen.people.import.problems': '들어가지 않은 줄',
  'core.screen.people.import.row': '{row}번째 줄',
  'core.screen.people.import.runs': '가져오기 이력',
  'core.screen.people.import.runs.empty': '아직 가져오기를 한 적이 없습니다',
  'core.screen.people.import.failed': '가져오지 못했습니다.',
  'core.screen.people.import.close': '닫기',

  // 회사 로고·직인 — 명세서 PDF 에 찍힌다.
  'core.screen.company.brand': '로고와 직인',
  'core.screen.company.logo': '로고',
  'core.screen.company.seal': '직인',
  'core.screen.company.brand.description': '급여명세서·퇴직정산 명세서 PDF 에 찍힙니다. 로고는 문서 왼쪽 위, 직인은 회사 이름 옆입니다.',
  'core.screen.company.brand.empty': '아직 올리지 않았습니다',
  'core.screen.company.brand.upload': '올리기',
  'core.screen.company.brand.uploading': '올리는 중…',
  'core.screen.company.brand.failed': '올리지 못했습니다.',
};
